// [SCOPE 051 demo / 053] Authored fr-FR bundle.
//
// Demonstrates the localization framework end-to-end: the keys translated here
// render in French when the locale is fr-FR; any key NOT present falls back to
// en-US per key (FR-002). Currently scoped to the landing page (the wired slice)
// plus common/settings; the full app-wide extraction is spec 052.
//
// Methodology/lifecycle terms and brand/compliance names (wxKanban, SOC 2, HIPAA,
// GDPR, HITRUST) are intentionally left untranslated.
import type { PartialLocaleBundle } from "./en-US";

export const frFR: PartialLocaleBundle = {
  common: {
    save: "Enregistrer",
    cancel: "Annuler",
    close: "Fermer",
    delete: "Supprimer",
    edit: "Modifier",
    loading: "Chargement…",
    saving: "Enregistrement…",
    saved: "Enregistré",
    error: "Une erreur s'est produite",
    retry: "Réessayer",
    yes: "Oui",
    no: "Non",
  },
  settings: {
    language: {
      title: "Langue",
      description: "Choisissez la langue de l'interface.",
      help: "Les langues non traduites reviennent à l'anglais tant que leurs textes ne sont pas rédigés.",
      saved: "Langue mise à jour.",
    },
  },
  landing: {
    nav: {
      home: "Accueil",
      features: "Fonctionnalités",
      pricing: "Tarifs",
      docs: "Aide/Tutoriels",
      signIn: "Connexion",
    },
    hero: {
      subtitle: "Système de gestion de projet avec IA",
      description:
        "wxKanban est un système complet de gestion de projet conçu autour de l'IA, avec un kit de démarrage prêt pour la production, adapté aux équipes de toute taille. Pensé pour la sécurité, la conformité et des intégrations fluides.",
      getStarted: "Commencer",
      learnMore: "En savoir plus",
    },
    install: {
      eyebrow: "Prêt en ~30 minutes",
      heading: "Installation et configuration",
      description:
        "Nouveau sur wxKanban ? Notre guide pas à pas vous accompagne d'un ordinateur vierge à une configuration fonctionnelle dans VS Code — installation de Claude Code, ajout du Dev Cockpit et mise en place de votre premier projet. Aucune expérience préalable requise.",
      step1: {
        title: "Installer les outils",
        body: "VS Code, Node.js et Claude Code — avec des commandes à copier-coller.",
      },
      step2: {
        title: "Ajouter le Dev Cockpit",
        body: "Installez l'extension et connectez-la à votre projet wxKanban.",
      },
      step3: {
        title: "Lancer votre premier workflow",
        body: "Scope, spec et build — guidé à travers le cycle de vie en 6 phases.",
      },
      cta: "Voir les instructions de configuration",
    },
    features: {
      heading: "Des fonctionnalités puissantes",
      subheading:
        "Tout ce dont vous avez besoin pour gérer vos projets, suivre le temps et collaborer avec votre équipe",
      aiPowered: {
        title: "Propulsé par l'IA",
        description:
          "Suggestions de tâches intelligentes et optimisation des workflows, propulsées par votre outil d'IA préféré pour le développement",
      },
      security: {
        title: "Sécurité pour l'entreprise",
        description:
          "Conforme SOC 2, HIPAA et RGPD, avec chiffrement AES-256-GCM",
      },
      integration: {
        title: "Intégration multi-systèmes",
        description:
          "Connectez Jira, Monday.com, Asana, Trello et d'autres plateformes de gestion de projet en toute fluidité",
      },
      conversion: {
        title: "Conversion d'applications héritées",
        intro: "Convertissez vos applications héritées telles que :",
        more: "et plus encore",
      },
      collaboration: {
        title: "Collaboration d'équipe",
        item1:
          "Gestion d'équipe en temps réel avec contrôle d'accès basé sur les rôles",
        item2:
          "Ajoutez les documents SDLC de votre entreprise pour les analyser en vue de la conformité",
        item3:
          "Attribuez le SDLC à chaque membre de l'équipe pour respecter les contrôles d'audit",
        item4:
          "Laissez l'IA vous aider à respecter toutes les exigences d'audit",
      },
      lifecycle: {
        title: "Cycle de vie en 6 phases",
        item1: "Méthodologie qualité structurée, de Design à Release",
        item2:
          "Commandes open source et Claude pour piloter l'ensemble du processus de développement",
        item3:
          "Mettez à jour automatiquement les specs et les tâches sur votre système de gestion de projet",
        item4: "Flux automatiques pour garantir la conformité aux audits",
        item5: "Rapport d'audit complet à chaque version",
        footer:
          "Ce que vous écrivez et générez dans VS Code — désormais dans un véritable système de gestion de projet",
      },
    },
    pricing: {
      heading: "Une tarification simple et transparente",
      subheading:
        "Choisissez le forfait idéal pour votre équipe. Toujours avec la possibilité de monter ou descendre en gamme",
      mostPopular: "LE PLUS POPULAIRE",
      perMonth: "/mois",
      free: "Gratuit",
      faqText: "Des questions sur la tarification ?",
      contactSales: "Contactez notre équipe commerciale",
      tiers: {
        single: {
          name: "Utilisateur unique",
          userLimit: "1 utilisateur",
          f1: "Projets illimités",
          f2: "Une intégration de gestion de projet",
          f3: "Support par e-mail",
          cta: "Commencer",
        },
        smb: {
          name: "Petite/Moyenne entreprise",
          userLimit: "Jusqu'à 5 utilisateurs",
          f1: "Certifié SOC 2 et HIPAA",
          f2: "Une seule entreprise",
          f3: "Gestion d'équipe",
          f4: "Support par e-mail",
          cta: "Commencer",
        },
        consultant: {
          name: "Consultant",
          userLimit: "Équipe illimitée",
          f1: "Membres d'équipe illimités",
          f2: "Plusieurs connexions de gestion de projet",
          f3: "Suivi du temps avancé",
          f4: "Génération de factures",
          f5: "Workflows personnalisés",
          f6: "Accès à l'API",
          cta: "S'inscrire",
        },
        enterprise: {
          name: "Entreprise",
          priceUnit: "+ 5 $/utilisateur au-delà de 25",
          userLimit: "25 utilisateurs et plus",
          f1: "25 utilisateurs inclus",
          f2: "5 $/mois par utilisateur actif au-delà de 25",
          f3: "Tout en illimité",
          f4: "Support dédié",
          f5: "Intégrations personnalisées",
          f6: "Garantie de SLA",
          cta: "Commencer",
        },
      },
    },
    compliance: {
      heading: "Une sécurité de niveau entreprise",
      subheading:
        "Conçu avec la conformité et la sécurité comme exigences fondamentales",
      soc2: {
        name: "Aligné sur SOC 2",
        description: "Conçu selon les Trust Services Criteria de SOC 2",
      },
      hipaa: {
        name: "Contrôles orientés HIPAA",
        description:
          "Mesures de protection inspirées des normes sur les informations de santé protégées",
      },
      gdpr: {
        name: "Prêt pour le RGPD",
        description: "Contrôles de confidentialité et de protection des données intégrés",
      },
      encryption: {
        title: "Chiffrement au repos et en transit",
        description: "Chiffrement AES-256-GCM pour toutes les données",
      },
      auth: {
        title: "Authentification sécurisée",
        description:
          "Hachage bcrypt avec jetons JWT et rotation des jetons de rafraîchissement",
      },
      audit: {
        title: "Journalisation d'audit",
        description:
          "Piste d'audit complète pour la conformité et la transparence",
      },
      rbac: {
        title: "Contrôle d'accès basé sur les rôles",
        description:
          "Autorisations fines pour les équipes et les données",
      },
      trustLead: "Conçu pour la confiance",
      trustBody:
        "La sécurité de vos données est notre priorité absolue. Nous nous appuyons sur des normes de sécurité reconnues et vérifions régulièrement nos contrôles.",
    },
    cta: {
      headline: "Prêt à transformer votre gestion de projet ?",
      subhead: "Rejoignez des milliers d'équipes qui utilisent wxKanban pour fluidifier leurs workflows",
      getStarted: "Commencer",
      viewPricing: "Voir les tarifs",
    },
    footer: {
      tagline: "Gestion de projet propulsée par l'IA pour les équipes modernes",
      product: "Produit",
      company: "Entreprise",
      legal: "Mentions légales",
      features: "Fonctionnalités",
      pricing: "Tarifs",
      documentation: "Documentation",
      about: "À propos",
      blog: "Blog",
      contact: "Contact",
      bio: "Biographie",
      privacy: "Confidentialité",
      terms: "Conditions",
      security: "Sécurité",
      rights: "© 2026 wxKanban. Tous droits réservés.",
    },
  },
  login: {
    title: "Bon retour",
    subtitle: "Connectez-vous à votre compte wxKanban",
    email: "E-mail",
    emailPlaceholder: "vous@entreprise.com",
    password: "Mot de passe",
    rememberMe: "Se souvenir de moi",
    forgotPassword: "Mot de passe oublié ?",
    signIn: "Se connecter",
    signingIn: "Connexion en cours…",
    genericError: "Une erreur inattendue s'est produite. Veuillez réessayer.",
    noAccount: "Vous n'avez pas de compte ?",
    register: "Inscrivez votre entreprise",
    tagline: "Gestion de tâches multiplateforme",
    emailLabel: "Adresse e-mail",
    showPasswordAria: "Maintenir pour afficher le mot de passe",
    registerLink: "Inscrivez-vous ici",
    connectPmPrompt: "Ou connectez un système de gestion de projet pour commencer :",
    loginFailed: "Échec de la connexion",
  },
  forgotPassword: {
    title: "Mot de passe oublié",
    description: "Saisissez votre e-mail et nous vous enverrons un lien pour réinitialiser votre mot de passe.",
    successMessage: "Si un compte existe avec cet e-mail, un lien de réinitialisation du mot de passe a été envoyé. Vérifiez votre boîte de réception.",
    backToSignIn: "Retour à la connexion",
    emailLabel: "Adresse e-mail",
    emailPlaceholder: "vous@exemple.com",
    submit: "Envoyer le lien de réinitialisation",
    submitting: "Envoi...",
    errorFailed: "Échec de l'envoi du lien de réinitialisation",
  },
  resetPassword: {
    title: "Réinitialiser le mot de passe",
    subtitle: "Saisissez votre nouveau mot de passe ci-dessous.",
    newPassword: "Nouveau mot de passe",
    confirmNewPassword: "Confirmer le nouveau mot de passe",
    updatePassword: "Mettre à jour le mot de passe",
    updating: "Mise à jour...",
    successMessage: "Votre mot de passe a été réinitialisé avec succès. Redirection vers la connexion...",
    backToSignIn: "Retour à la connexion",
    errMissingLink: "Ce lien de réinitialisation du mot de passe est invalide ou incomplet.",
    errMissingToken: "Le jeton de réinitialisation est manquant. Veuillez utiliser le lien de votre e-mail.",
    errPasswordMin: "Le mot de passe doit contenir au moins 8 caractères",
    errPasswordMismatch: "Les mots de passe ne correspondent pas",
    errFailed: "Échec de la réinitialisation du mot de passe",
  },
  changePassword: {
    title: "Changez votre mot de passe",
    welcome: "Bienvenue, {{name}} ! Pour des raisons de sécurité, veuillez changer votre mot de passe temporaire avant de continuer.",
    currentLabel: "Mot de passe actuel (temporaire)",
    currentPlaceholder: "Saisissez votre mot de passe temporaire",
    newLabel: "Nouveau mot de passe",
    newPlaceholder: "Créez un nouveau mot de passe (min. 8 caractères)",
    confirmLabel: "Confirmer le nouveau mot de passe",
    confirmPlaceholder: "Confirmez votre nouveau mot de passe",
    submit: "Changer le mot de passe et continuer",
    needHelp: "Besoin d'aide ? Contactez votre administrateur ou l'",
    supportTeam: "équipe d'assistance",
    errMinLength: "Le nouveau mot de passe doit contenir au moins 8 caractères",
    errMismatch: "Les mots de passe ne correspondent pas",
    errSameAsOld: "Le nouveau mot de passe doit être différent de l'actuel",
    errFailed: "Échec du changement de mot de passe",
    successRedirect: "Mot de passe changé avec succès ! Redirection vers le tableau de bord...",
  },
  appnav: {
    dashboard: "Tableau de bord",
    teamMembers: "Membres de l'équipe",
    projects: "Projets",
    pmIntegrations: "Intégrations PM",
    myTasks: "Mes tâches",
    workHub: "Espace de travail",
    timeBilling: "Temps et facturation",
    invoices: "Factures",
    reports: "Rapports",
    settings: "Paramètres",
    gettingStarted: "Prise en main",
    manual: "Manuel", // [SCOPE 141 / T016]
    help: "Aide",
    signOut: "Déconnexion",
  },
  register: {
    next: "Suivant",
    loading: "Chargement...",
    back: "Retour",
    companyTitle: "Informations sur l'entreprise",
    companySubtitle: "Parlez-nous de votre entreprise afin que nous puissions configurer votre facturation et vos communications",
    companyNameLabel: "Nom de l'entreprise *",
    billingEmailLabel: "E-mail de facturation *",
    billingAddressLabel: "Adresse de facturation *",
    countryLabel: "Pays *",
    phoneLabel: "Numéro de téléphone *",
    referredByLabel: "Parrainé par (facultatif)",
    referredByHint: "Si un distributeur vous a recommandé, saisissez son code.",
    errCompanyNameRequired: "Le nom de l'entreprise est requis",
    errCompanyNameMin: "Le nom de l'entreprise doit comporter au moins 2 caractères",
    errCompanyNameMax: "Le nom de l'entreprise doit comporter moins de 255 caractères",
    errBillingEmailRequired: "L'e-mail de facturation est requis",
    errEmailInvalid: "Veuillez saisir une adresse e-mail valide",
    errBillingAddressRequired: "L'adresse de facturation est requise",
    errBillingAddressMin: "L'adresse de facturation doit comporter au moins 10 caractères",
    errBillingAddressMax: "L'adresse de facturation doit comporter moins de 500 caractères",
    errCountryRequired: "Le pays est requis",
    errCountryInvalid: "Veuillez sélectionner un code de pays valide",
    errPhoneRequired: "Le numéro de téléphone est requis",
    errPhoneMin: "Le numéro de téléphone doit comporter au moins 10 caractères",
    errPhoneMax: "Le numéro de téléphone doit comporter moins de 20 caractères",
    adminTitle: "Créer un compte administrateur",
    adminSubtitle: "Configurez votre compte administrateur pour accéder à wxKanban. C'est le premier utilisateur qui sera créé pour votre entreprise.",
    emailLabel: "Adresse e-mail *",
    firstNameLabel: "Prénom *",
    lastNameLabel: "Nom (facultatif)",
    passwordLabel: "Mot de passe *",
    pwReqTitle: "Exigences du mot de passe :",
    pwReqLength: "Au moins 12 caractères",
    pwReqUpper: "Au moins une lettre majuscule",
    pwReqLower: "Au moins une lettre minuscule",
    pwReqNumber: "Au moins un chiffre",
    pwReqSpecial: "Au moins un caractère spécial (!@#$%^&*)",
    termsPrefix: "En créant un compte, vous acceptez nos",
    termsOfService: "Conditions d'utilisation",
    and: "et",
    privacyPolicy: "Politique de confidentialité",
    creatingAccount: "Création du compte...",
    completeRegistration: "Finaliser l'inscription",
    errEmailRequired: "L'e-mail est requis",
    errPasswordRequired: "Le mot de passe est requis",
    errPasswordRequirements: "Le mot de passe ne remplit pas toutes les exigences",
    errFirstNameRequired: "Le prénom est requis",
    errFirstNameLength: "Le prénom doit comporter entre 1 et 100 caractères",
    errLastNameLength: "Le nom doit comporter entre 1 et 100 caractères",
    paymentMethod: "Moyen de paiement",
    preparingPayment: "Préparation du paiement sécurisé…",
    initPaymentError: "Échec de l'initialisation du paiement",
    subscribingTo: "Vous vous abonnez à",
    at: "à",
    perMonth: "/mois",
    stripeNotice: "Les détails de la carte sont gérés directement par Stripe. Aucune donnée sensible ne transite par nos serveurs.",
    paymentFailedCard: "Le paiement a échoué. Veuillez vérifier votre carte et réessayer.",
    paymentFailed: "Le paiement a échoué",
    processing: "Traitement…",
    completePayment: "Finaliser le paiement",
    startPromo: "Démarrer la période gratuite",
    inviteChecking: "Vérification de votre invitation…",
    inviteInvalid: "Ce lien d'invitation n'est pas valide. Il a peut-être déjà été utilisé, révoqué ou expiré.",
    inviteFreeMonths: "Vous avez été invité : vos {{days}} premiers jours sur {{plan}} sont gratuits.",
    inviteBoundTo: "Cette invitation est destinée à {{email}} — vous vous inscrirez avec cette adresse.",
    promoFreeForDays: "Vos {{days}} premiers jours sont gratuits.",
    promoFirstChargeOn: "Votre premier paiement de {{amount}} aura lieu le {{date}}.",
    promoFirstChargeAfter: "Votre premier paiement de {{amount}} aura lieu après {{days}} jours.",
    promoCardNotChargedToday: "Votre carte ne sera pas débitée aujourd'hui. Nous la conservons afin que votre abonnement se poursuive automatiquement.",
    tierSingleUser: "Utilisateur unique",
    tierSmallBusiness: "Petite/Moyenne entreprise",
    tierConsultant: "Consultant",
    tierEnterprise: "Entreprise",
    tierFree: "Gratuit",
    confTitle: "Confirmez votre inscription",
    confSubtitle: "Veuillez vérifier vos informations avant de finaliser l'inscription",
    confCompanyDesc: "Détails sur votre organisation",
    confCompanyName: "Nom de l'entreprise",
    confBillingEmail: "E-mail de facturation",
    subscriptionPlan: "Forfait d'abonnement",
    subscriptionPlanDesc: "Votre niveau et tarif sélectionnés",
    selectedPlan: "Forfait sélectionné",
    monthlyPrice: "Prix mensuel",
    adminAccount: "Compte administrateur",
    adminAccountDesc: "Vos identifiants de connexion administrateur",
    nameLabel: "Nom",
    emailShort: "E-mail",
    termsClickPrefix: "En cliquant sur « Finaliser l'inscription », vous acceptez nos",
    title: "Bienvenue sur wxKanban",
    settingUp: "Configuration de votre compte",
    account: "",
    stepCompany: "Entreprise",
    stepPayment: "Paiement",
    stepAdmin: "Admin",
    successTitle: "Entreprise créée avec succès !",
    verificationSent: "Un e-mail de vérification a été envoyé à",
    whatsNext: "Prochaines étapes :",
    next1: "Vérifiez votre e-mail et cliquez sur le lien de vérification",
    next2: "Connectez-vous à votre nouveau compte wxKanban",
    next3: "Configurez votre équipe et commencez à gérer vos projets",
    goToLogin: "Aller à la connexion",
    createAnother: "Créer un autre compte",
    haveAccount: "Vous avez déjà un compte ?",
    signIn: "Se connecter",
  },
  settingsPage: {
    title: "Paramètres",
    subtitle: "Gérez les paramètres de votre entreprise et de votre compte",
    tabCompany: "Entreprise",
    tabProfile: "Profil",
    tabNotifications: "Notifications",
    tabSecurity: "Sécurité",
    companyInfo: "Informations sur l'entreprise",
    companyInfoDesc: "Mettez à jour les détails et les informations de facturation de votre entreprise",
    profileInfo: "Informations du profil",
    profileInfoDesc: "Mettez à jour vos informations personnelles et vos préférences",
    language: "Langue",
    changePassword: "Changer le mot de passe",
    notificationPrefs: "Préférences de notification",
    securitySettings: "Paramètres de sécurité",
    saveChanges: "Enregistrer les modifications",
    saveProfile: "Enregistrer le profil",
  },
  dashboard: {
    failedToFetch: "Échec de la récupération : {{status}}",
    failedToLoadPendingTasks: "Échec du chargement des tâches en attente",
    adminTitle: "Tableau de bord administrateur",
    lastUpdated: "Dernière mise à jour : {{time}}",
    welcome: "Bienvenue, {{email}}",
    panelCompany: "Entreprise",
    totalUsers: "Utilisateurs au total",
    activeUsers: "Utilisateurs actifs",
    byRole: "Par rôle :",
    panelProjects: "Projets",
    totalProjects: "Projets au total",
    active: "Actifs",
    panelTeamSummary: "Résumé de l'équipe",
    total: "Total",
    panelFinancials: "Finances",
    revenue: "Revenus (ce mois-ci)",
    invoices: "Factures",
    outstanding: "En attente",
    panelActivity: "Fil d'activité",
    loadingActivities: "Chargement des activités…",
    noActivity: "Aucune activité récente",
    panelPendingTasks: "Tâches en attente",
    loading: "Chargement…",
    noActiveProjects: "Aucun projet actif.",
    colProject: "Projet",
    colOpen: "Ouvertes",
    colInProgress: "En cours",
  },
  projects: {
    title: "Projets",
    noProjects: "Aucun projet pour l'instant",
    count: "{{count}} projets",
    countOne: "1 projet",
    showAll: "Tout afficher",
    myProjects: "Mes projets",
    newProject: "Nouveau projet",
    searchPlaceholder: "Rechercher des projets…",
    sort: "Trier :",
    sortUpdated: "Modifié",
    sortCreated: "Créé",
    sortName: "Nom",
    loadError: "Impossible de charger les projets",
    noMatch: "Aucun projet ne correspond aux filtres actuels",
    clearFilters: "Effacer les filtres",
    createFirst: "Créez votre premier projet",
  },
  tasks: {
    back: "Retour au tableau de bord",
    title: "Mes tâches",
    lastSync: "Dernière synchro :",
    syncNow: "Synchroniser",
    syncing: "Synchronisation…",
    allSources: "Toutes les sources",
    allPriorities: "Toutes les priorités",
    priorityCritical: "Critique",
    priorityHigh: "Haute",
    priorityMedium: "Moyenne",
    priorityLow: "Basse",
    allStatuses: "Tous les statuts",
    statusOpen: "Ouverte",
    statusInProgress: "En cours",
    statusCompleted: "Terminée",
    statusOrphaned: "Orpheline",
    sortUpdated: "Récemment modifiées",
    sortDue: "Date d'échéance",
    sortPriority: "Priorité",
    sortTitle: "Titre",
    loading: "Chargement des tâches…",
    noTasks: "Aucune tâche pour l'instant",
    connectHint1: "Connectez un système PM depuis",
    integrations: "Intégrations",
    connectHint2: "et cliquez sur Synchroniser pour importer vos tâches assignées.",
  },
  integrations: {
    title: "Intégrations des systèmes de gestion de projet",
    subtitle: "Connectez vos outils de gestion de projet à wxKanban",
    viewTasks: "Voir mes tâches →",
    connectedSuccess: "✓ Système de gestion de projet connecté avec succès !",
    loading: "Chargement des intégrations...",
    connectedSince: "Connecté depuis le {{date}}",
    notConnected: "Non connecté",
    active: "✓ Actif",
    disconnect: "Déconnecter",
    connect: "Connecter",
    needHelp: "Besoin d'aide pour la connexion ?",
    helpText: "Chaque système de gestion de projet nécessite des identifiants OAuth. Consultez la documentation pour les instructions de configuration.",
    confirmDisconnect: "Déconnecter {{system}} ?",
  },
  invoices: {
    title: "Factures",
    subtitle: "Générez et gérez les factures clients à partir des temps facturables",
    generate: "Générer une facture",
    statTotal: "Total des factures",
    statRevenue: "Chiffre d'affaires total",
    statSent: "Factures envoyées",
    modalTitle: "Générer une nouvelle facture",
    modalDesc: "Sélectionnez la plage de dates pour les temps facturables",
    startDate: "Date de début",
    endDate: "Date de fin",
    generateConfirm: "Générer",
    history: "Historique des factures",
    emptyTitle: "Aucune facture générée pour le moment",
    emptyHint: "Créez votre première facture pour commencer",
    colNumber: "N° de facture",
    colDate: "Date",
    colBilledTo: "Facturé à",
    colHours: "Heures",
    colAmount: "Montant",
    colStatus: "Statut",
    colActions: "Actions",
    statusSent: "Envoyée",
    statusDraft: "Brouillon",
    na: "N/D",
    detailTitle: "Facture {{number}}",
    generatedOn: "Générée le {{date}}",
    hoursWorked: "Heures travaillées",
    totalAmount: "Montant total",
    downloadPdf: "Télécharger le PDF",
    sendInvoice: "Envoyer la facture",
    resend: "Renvoyer",
  },
  reports: {
    title: "Rapports",
    subtitle: "Consultez des analyses détaillées sur le suivi du temps, les projets et la performance de l'équipe",
    tabTime: "Rapports de temps",
    tabProjects: "Rapports de projets",
    dateRange: "Plage de dates",
    exportCsv: "Exporter en CSV",
    timeByMember: "Temps par membre de l'équipe",
    timeByMemberDesc: "Répartition des heures enregistrées par chaque membre de l'équipe",
    selectDateRange: "Sélectionnez une plage de dates pour afficher les rapports de temps",
    colMember: "Membre de l'équipe",
    colTotalHours: "Heures totales",
    colBillableHours: "Heures facturables",
    colBillablePct: "% facturable",
    projectPerformance: "Performance des projets",
    projectPerformanceDesc: "Vue d'ensemble de tous les projets et de leur progression",
    noProjects: "Aucun projet trouvé",
    colProject: "Projet",
    colHours: "Heures",
    colTasks: "Tâches",
    colCompletion: "Achèvement",
    colTeamSize: "Taille de l'équipe",
  },
  workhub: {
    summaryTotal: "Tâches totales",
    summaryPastDue: "En retard",
    summaryAging: "Plus de 30 jours",
    changeStatus: "Modifier le statut de la tâche",
    task: "Tâche",
    newStatus: "Nouveau statut",
    blockedDesc: "une dépendance externe empêche la progression.",
    suspendedDesc: "en pause pour réévaluation.",
    reason: "Motif",
    reasonPlaceholder: "Expliquez pourquoi cette tâche est mise en pause...",
    reasonRequired: "Un motif est requis pour les tâches bloquées ou suspendues.",
    updateStateError: "Échec de la mise à jour de l'état de la tâche",
    unknownError: "Erreur inconnue",
    submit: "Envoyer",
    saving: "Enregistrement...",
    cancel: "Annuler",
    saveTimeEntry: "Enregistrer l'entrée de temps",
    duration: "Durée",
    billableQ: "Facturable ?",
    billable: "Facturable",
    nonBillable: "Non facturable",
    notesOptional: "Notes (facultatif)",
    notesPlaceholder: "Sur quoi avez-vous travaillé ?",
    saveEntry: "Enregistrer l'entrée",
    saveEntryError: "Échec de l'enregistrement de l'entrée",
    taskActivity: "Activité de la tâche",
    loadingActivity: "Chargement de l'activité de la tâche...",
    loadDetailError: "Échec du chargement des détails de la tâche",
    timeEntries: "Entrées de temps ({{count}})",
    noTimeEntries: "Aucune entrée de temps pour le moment.",
    invoiced: "Facturé",
    statusHistory: "Historique des statuts ({{count}})",
    noStateChanges: "Aucun changement d'état enregistré.",
    pmSyncState: "État de synchronisation PM",
    syncStatus: "Statut :",
    syncUnknown: "inconnu",
    lastSyncLabel: "Dernière synchro : {{time}}",
    retries: "Nouvelles tentatives : {{count}}",
    errorLabel: "Erreur : {{message}}",
    resolved: "Résolu : {{time}}",
    aiBadge: "IA",
    manualBadge: "Manuel",
    running: "en cours",
    title: "Centre de travail du consultant",
    subtitle: "File de tâches unifiée avec filtrage par projet et suivi du temps en un clic.",
    allProjects: "Tous les projets",
    timerRunning: "Minuteur en cours",
    stopTimer: "Arrêter le minuteur",
    inactivityTitle: "Le minuteur tourne depuis plus de 8 heures",
    inactivityBody: "S'agit-il toujours d'un travail actif ? Arrêtez le minuteur si vous vous êtes absenté.",
    keepRunning: "Continuer",
    loadingQueue: "Chargement de la file du consultant...",
    loadError: "Échec du chargement des données du centre de travail.",
    assignedQueue: "File de travail assignée",
    noTasks: "Aucune tâche assignée trouvée pour ce filtre.",
    compliance: "Conformité",
    activity: "Activité",
    blocked: "Bloqué",
    suspended: "Suspendu",
    stop: "Arrêter",
    startTimer: "Démarrer le minuteur",
    activeTask: "Tâche active",
    billablePreview: "Aperçu facturable prêt à facturer",
    complianceFlaggedTasks: "Tâches signalées pour conformité : {{count}}",
    loadingPreview: "Chargement de l'aperçu facturable...",
    previewError: "Impossible de charger l'aperçu facturable.",
    previewSummary: "Entrées : {{count}} • Total des heures facturables : {{hours}}",
    sourceBreakdown: "Répartition par source :",
    manual: "Manuel :",
    ai: "IA :",
    complianceReviewPanel: "Panneau de révision de conformité",
    loadingFlags: "Chargement des signalements de conformité...",
    flagsError: "Impossible de charger les signalements de conformité.",
    noFlags: "Aucun signalement de conformité ouvert.",
  },
  billing: {
    title: "Configuration de la facturation",
    subtitle: "Configurez les taux horaires pour le suivi du temps et la facturation. Les taux suivent une hiérarchie : Utilisateur → Projet → Défaut de l'entreprise.",
    fetchError: "Échec de la récupération des taux de facturation",
    updateError: "Échec de la mise à jour du taux",
    updateSuccess: "Taux de facturation mis à jour avec succès",
    invalidRate: "Veuillez saisir un taux valide",
    resolutionTitle: "Ordre de résolution des taux :",
    resolutionBody: "Lors du calcul des montants facturables, le système vérifie les taux dans cet ordre : 1) taux spécifique à l'utilisateur, 2) taux spécifique au projet, 3) taux par défaut de l'entreprise.",
    companyRate: "Taux par défaut de l'entreprise",
    companyRateDesc: "Taux de repli utilisé lorsqu'aucun taux utilisateur ou projet n'est défini",
    hourlyRate: "Taux horaire (USD)",
    projectRates: "Taux spécifiques aux projets",
    projectRatesDesc: "Remplacer le taux par défaut de l'entreprise pour des projets spécifiques",
    projectIdLabel: "ID du projet : {{id}}",
    userRates: "Taux spécifiques aux utilisateurs",
    userRatesDesc: "Remplacer tous les autres taux pour des membres d'équipe spécifiques",
    noTeamMembers: "Aucun membre d'équipe trouvé",
    customRate: "Taux personnalisé",
    usingDefault: "Utilise le taux par défaut",
    defaultPlaceholder: "Par défaut",
  },
  verification: {
    noToken: "Aucun jeton de vérification fourni. Veuillez vérifier le lien dans votre e-mail.",
    verifiedSuccess: "Votre adresse e-mail a été vérifiée avec succès !",
    verifyFailed: "Échec de la vérification de l'e-mail. Le lien a peut-être expiré.",
    unexpectedError: "Une erreur inattendue s'est produite. Veuillez réessayer ou contacter le support.",
    resendFailed: "Échec du renvoi de l'e-mail de vérification. Veuillez réessayer.",
    networkError: "Erreur réseau. Veuillez réessayer.",
    titleLoading: "Vérification de votre e-mail...",
    titleSuccess: "E-mail vérifié !",
    titleError: "Échec de la vérification",
    descLoading: "Veuillez patienter pendant que nous vérifions votre adresse e-mail",
    descSuccess: "Vous pouvez maintenant vous connecter à votre compte",
    descError: "Nous avons rencontré un problème avec votre vérification",
    signInNow: "Se connecter maintenant",
    resending: "Renvoi en cours...",
    resendButton: "Renvoyer l'e-mail de vérification",
    resentNotice: "E-mail de vérification renvoyé ! Vérifiez votre boîte de réception.",
    backToSignIn: "Retour à la connexion",
    redirecting: "Redirection vers la connexion dans 3 secondes...",
  },
  invitePage: {
    errorNoToken: "Aucun jeton d'invitation fourni",
    errorInvalidExpired: "Invitation non valide ou expirée",
    errorPasswordMismatch: "Les mots de passe ne correspondent pas",
    errorPasswordTooShort: "Le mot de passe doit comporter au moins 8 caractères",
    errorAcceptFailed: "Échec de l'acceptation de l'invitation",
    errorUnexpected: "Une erreur inattendue s'est produite",
    invalidTitle: "Invitation non valide",
    invalidDescription: "Ce lien d'invitation n'est pas valide ou a expiré.",
    invalidContactPrompt: "Veuillez contacter la personne qui vous a invité pour demander une nouvelle invitation.",
    goToSignIn: "Aller à la connexion",
    acceptedTitle: "Bienvenue dans l'équipe !",
    acceptedDescription: "Votre compte a été créé avec succès.",
    acceptedBody: "Vous pouvez maintenant vous connecter pour accéder à l'espace de travail wxKanban de votre entreprise.",
    signInNow: "Se connecter maintenant",
    joinTitle: "Rejoindre {{companyName}}",
    invitedBy: "Vous avez été invité par {{inviterName}} à rejoindre en tant que {{role}}",
    emailLabel: "E-mail",
    firstNameLabel: "Prénom",
    lastNameLabel: "Nom",
    passwordLabel: "Mot de passe",
    confirmPasswordLabel: "Confirmer le mot de passe",
    creatingAccount: "Création du compte...",
    acceptAndCreate: "Accepter l'invitation et créer un compte",
    alreadyHaveAccount: "Vous avez déjà un compte ?",
    signInHere: "Connectez-vous ici",
  },
  inviteAccept: {
    subtitle: "Invitation à l'équipe",
    validating: "Validation de l'invitation...",
    expiredTitle: "Invitation expirée",
    expiredBody: "Ce lien d'invitation a expiré. Demandez à votre administrateur de le renvoyer.",
    usedTitle: "Invitation déjà utilisée",
    invalidTitle: "Invitation non valide",
    usedBody: "Cette invitation a déjà été acceptée. Essayez de vous connecter.",
    invalidBody: "Ce lien d'invitation est non valide ou a expiré.",
    goToLogin: "Aller à la connexion",
    successTitle: "Compte créé !",
    successBody: "Votre compte a été configuré. Vous pouvez maintenant vous connecter à wxKanban.",
    logIn: "Se connecter",
    joinCompany: "Rejoindre {{company}}",
    creatingAccountFor: "Création du compte pour",
    fullNameLabel: "Nom complet",
    passwordLabel: "Mot de passe",
    passwordPlaceholder: "8 caractères min.",
    confirmPasswordLabel: "Confirmer le mot de passe",
    confirmPasswordPlaceholder: "Ressaisissez le mot de passe",
    timezoneLabel: "Fuseau horaire",
    timezoneHint: "Détecté automatiquement depuis votre navigateur",
    creatingAccount: "Création du compte...",
    createAccount: "Créer un compte",
    alreadyHaveAccount: "Vous avez déjà un compte ?",
    signIn: "Se connecter",
    errorPasswordMismatch: "Les mots de passe ne correspondent pas",
    errorPasswordTooShort: "Le mot de passe doit comporter au moins 8 caractères",
    errorCreateAccountFailed: "Échec de la création du compte",
    errorUnknown: "Erreur inconnue",
  },
  inviteTeam: {
    title: "Inviter des membres de l'équipe",
    subtitle: "Ajoutez des collègues pour collaborer sur vos projets",
    sendInvitations: "Envoyer des invitations",
    sendInvitationsDesc: "Choisissez comment inviter les membres de votre équipe",
    singleInvite: "Invitation unique",
    bulkUpload: "Import en masse",
    sendMore: "Envoyer d'autres invitations",
    viewTeam: "Voir l'équipe",
    tipsTitle: "Conseils pour inviter des membres de l'équipe",
    tipEmails: "Veillez à utiliser les bonnes adresses e-mail pour éviter les échecs de distribution",
    tipRoles: "Choisissez les rôles appropriés : Administrateur (accès complet), Manager (peut gérer les projets) ou Membre (peut consulter et réaliser les tâches)",
    tipCsv: "Pour les imports en masse, utilisez un fichier CSV avec les colonnes : email, usertype, firstName, lastName",
    tipExpiry: "Les invitations expirent après 7 jours si elles ne sont pas acceptées",
  },
  teamDashboard: {
    title: "Mon tableau de bord",
    welcomeBack: "Bon retour, {{email}}",
    assignedProjects: "Projets attribués",
    openTasks: "Tâches ouvertes",
    hoursThisWeek: "Heures cette semaine",
    noProjectsAssigned: "Aucun projet ne vous est encore attribué.",
    workHubTitle: "Espace de travail du consultant",
    workHubDescription: "Ouvrez votre file de tâches unifiée et suivez le temps directement sur le travail attribué.",
    openWorkHub: "Ouvrir l'espace de travail",
  },
  distributorSignup: {
    errorPasswordsMismatch: "Les mots de passe ne correspondent pas",
    errorPasswordTooShort: "Le mot de passe doit comporter au moins 8 caractères",
    errorAcceptTerms: "Veuillez accepter les conditions de distributeur",
    errorRegistrationFailed: "Échec de l'inscription",
    errorUnknown: "Erreur inconnue",
    successHeading: "Tout est prêt",
    successSignedInPrefix: "Votre compte distributeur gratuit est prêt. Vous êtes connecté en tant que",
    referralCodeLabel: "Votre code de parrainage",
    copyCodeTitle: "Copier le code",
    referralCodeHelp: "Partagez ce code avec les personnes que vous parrainez. Lorsqu'elles le saisissent lors de leur inscription, leur compte vous est attribué.",
    goToDashboard: "Accéder à votre tableau de bord",
    introBannerBefore: "Devenez distributeur wxKanban. Configurez votre compte",
    introBannerFree: "gratuit",
    introBannerAfter: "ci-dessous — vous recevrez un code de parrainage à partager et vous serez connecté automatiquement une fois terminé.",
    fieldBusinessName: "Nom de l'entreprise *",
    fieldFullName: "Votre nom complet *",
    fieldEmail: "E-mail *",
    fieldPassword: "Mot de passe *",
    placeholderPassword: "Au moins 8 caractères",
    fieldConfirmPassword: "Confirmer le mot de passe *",
    placeholderConfirmPassword: "Ressaisissez le mot de passe",
    acceptTermsLabel: "J'accepte les conditions de distributeur. Mon compte est un compte mono-utilisateur gratuit — je ne suis pas abonné à un forfait payant.",
    creatingAccount: "Création du compte…",
    createAccountButton: "Créer un compte gratuit",
    pageSubtitle: "Inscription distributeur",
  },
  overviewPage: {
    loadFailed: "Échec du chargement du document de synthèse ({{status}})",
    unknownError: "Erreur inconnue",
    dashboard: "Tableau de bord",
    projectOverview: "Aperçu du projet",
    refresh: "Actualiser",
    loadErrorTitle: "Impossible de charger le document de synthèse",
  },
  projectWorkspace: {
    // [SCOPE 137 / T019] Change Impact mode (owner-set)
    impactModeTitle: "Mode d'impact des changements",
    impactModeHelp: "Comment la vérification d'impact du kit traite les modifications faites par l'IA dans ce projet. S'applique au kit de chaque développeur et l'emporte sur une valeur locale.",
    impactModeNotify: "Notifier : indiquer au développeur ce que touche une modification, sans jamais demander",
    impactModeBoundaries: "Limites : demander seulement quand une modification atteint un autre périmètre ou un test validé (par défaut)",
    impactModeAlways: "Toujours : demander dès qu’une modification touche autre chose",
    impactModeNotSet: "Non défini ici. Le kit de chaque développeur utilise sa valeur locale, ou Limites.",
    impactModeSetBy: "Défini par {{who}} le {{when}}",
    impactModeOwnerOnly: "Seul le propriétaire du projet ou un administrateur de l'entreprise peut le modifier.",
    impactModeDataLoss: "Une perte de données possible est arrêtée dans tous les modes.",
    impactModeError: "La modification n'a pas été enregistrée. Veuillez réessayer.",
    complianceTabSettings: "Paramètres",
    complianceTabDocuments: "Documents",
    complianceTabTasks: "Tâches",
    complianceTabAudit: "Audit",
    complianceTabReports: "Rapports",
    learningsGenerateFailed: "Échec ({{status}})",
    learningsPrintHint: "Utilisez l'impression du navigateur pour enregistrer en PDF",
    regenerate: "Régénérer",
    learningsIframeTitle: "Enseignements du projet",
    learningsNoneTitle: "Aucun rapport d'enseignements pour le moment",
    learningsRunPrefix: "Exécutez",
    learningsRunSuffix: "après avoir terminé le travail pour capturer les décisions, les corrections et les contrôles de conformité.",
    learningsThenGenerate: "Générez ensuite le rapport ci-dessous.",
    generating: "Génération…",
    generateLearningsReport: "Générer le rapport d'enseignements",
    errFailedLoadProject: "Échec du chargement du projet",
    confirmArchive: "Archiver ce projet ? Il sera masqué de la liste des projets.",
    confirmDelete: "Supprimer définitivement \"{{name}}\" ? Cela supprime le projet ainsi que toutes ses tâches, phases, specs et documents. Cette action est irréversible.",
    thisProject: "ce projet",
    errFailedDeleteProjectStatus: "Échec de la suppression du projet ({{status}})",
    errFailedDeleteProject: "Échec de la suppression du projet",
    errFailedUpdateProject: "Échec de la mise à jour du projet",
    errFailedUpdateAuditType: "Échec de la mise à jour du type d'audit",
    loadingProject: "Chargement du projet...",
    projectNotFound: "Projet introuvable",
    backToProjects: "Retour aux projets",
    projects: "Projets",
    clientEngagementTitle: "Accès via une mission active avec {{name}}",
    client: "Client",
    clientProjectBadge: "Projet client — {{name}}",
    editProject: "Modifier le projet",
    auditLabel: "Audit :",
    noAudit: "Aucun audit",
    archiveProject: "Archiver le projet",
    archive: "Archiver",
    deleteProjectTitle: "Supprimer définitivement le projet",
    delete: "Supprimer",
    tabLifecycle: "Cycle de vie",
    tabDevelopmentPlan: "Plan de développement",
    tabPortfolioAnalyzer: "Analyseur de portefeuille",
    tabCompliance: "Conformité",
    tabLearnings: "Enseignements",
    tabKit: "Kit",
    views: "Vues",
    viewMatrix: "Matrice",
    viewStack: "Stack",
    viewTheme: "Thème",
    viewBugReports: "Rapports de bogues",
    iframeLifecycle: "Cycle de vie du projet",
    iframeDevplan: "Plan de développement",
    phaseNotStarted: "Cette phase n'a pas encore commencé.",
    saveFailed: "Échec de l'enregistrement",
    saveErrorIntro: "L'erreur suivante s'est produite lors de l'enregistrement :",
    copyToClipboard: "Copier dans le presse-papiers",
    copy: "Copier",
    copyHint: "Cliquez sur \"Copier\" pour copier ce message d'erreur dans votre presse-papiers afin de le signaler.",
    close: "Fermer",
    copyAndClose: "Copier et fermer",
    fieldProjectName: "Nom du projet",
    fieldDescription: "Description",
    fieldHourlyRate: "Taux horaire ($)",
    fieldAllocatedHours: "Heures allouées",
    fieldPmSystems: "Systèmes PM",
    fieldSecurityClassification: "Classification de sécurité",
    fieldAuditType: "Type d'audit",
    phaseAssignments: "Attributions de phase",
    loadingTeamMembers: "Chargement des membres de l'équipe...",
    unassigned: "Non attribué",
    reassignPermissionNote: "Seuls ADMIN et TEAM_MANAGER peuvent réattribuer les phases.",
    cancel: "Annuler",
    saving: "Enregistrement...",
    saveChanges: "Enregistrer les modifications",
  },
  helpPage: {
    backToHelpCenter: "Retour au centre d'aide",
    examples: "Exemples",
    wasArticleHelpful: "Cet article vous a-t-il été utile ?",
    yesItHelped: "Oui, cela m'a aidé",
    noNotHelpful: "Non, pas utile",
    helpCenter: "Centre d'aide",
    // [SCOPE 138 / T011] "Learn wxKanban with your AI"
    teachTitle: "Apprenez wxKanban avec votre IA",
    teachHowTo: "Téléchargez les leçons, donnez start-here.md (ou tout le zip) à n’importe quel chat d’IA et dites « apprends-moi ». Chaque leçon vous guide pas à pas dans une tâche.",
    teachDownload: "Télécharger les leçons",
    teachVersion: "wxKanban {{version}} · {{count}} leçons",
    teachError: "Le téléchargement a échoué. Veuillez réessayer.",
    subtitle: "Trouvez des réponses, de la documentation et des guides pour wxKanban",
    searchPlaceholder: "Rechercher des articles d'aide, des commandes CLI ou des guides...",
    searchResults: "Résultats de recherche",
    searching: "Recherche en cours...",
    noResults: "Aucun résultat trouvé pour « {{query}} »",
    viewsCount: "{{count}} vues",
    topicsCount: "{{count}} sujets",
    moreCount: "+{{count}} de plus",
  },
  planHelp: {
    title: "Aide",
    subtitlePrefix: "Ce que fait chaque zone de wxKanban et ce que votre offre inclut. Affichage de",
    subtitleSuffix: "par défaut — choisissez une autre offre pour comparer.",
    yourPlan: "votre offre",
    included: "Inclus",
    upgradeRequired: "Mise à niveau requise",
    loading: "Chargement de votre offre…",
  },
  faqPage: {
    title: "Aide et FAQ",
    subtitle: "Parcourez les réponses ou posez une question et nous l'ajouterons à la FAQ.",
    panel: {
      heading: "Foire aux questions",
      loading: "Chargement…",
      empty: "Aucune FAQ pour le moment — posez une question ci-dessous.",
      askPrompt: "Vous n'avez pas trouvé votre réponse ? Demandez-nous",
      placeholder: "Saisissez votre question…",
      submit: "Envoyer la question",
      sending: "Envoi…",
      tooShort: "Veuillez saisir une question (au moins 5 caractères).",
      submitError: "Impossible d'envoyer votre question.",
      thanks: "Merci — nous y répondrons.",
    },
  },
  aboutPage: {
    title: "À propos de wxKanban",
    lede: "wxKanban est une manière de développer des logiciels avec l'IA pilotée par les spécifications : elle vous apprend à travailler comme les professionnels, en menant une application à travers tout le cycle de vie, d'une idée brouillonne à un résultat propre, testé et livré, sur des fondations qui vous appartiennent vraiment.",
    whatItIsHeading: "Ce que c'est",
    whatItIsPara1: "wxKanban est un environnement de développement piloté par les spécifications. Vous progressez à travers un cycle de vie guidé en six phases — Conception → Implémentation → QA → Tests humains → Bêta → Publication — où rien n'est construit sans une spécification approuvée, et chaque changement est traçable jusqu'à la spécification qui l'a autorisé. Tout aussi important, il maintient vos spécifications et votre documentation cohérentes à mesure que le logiciel évolue : les changements futurs sont vérifiés par rapport à ce qui est déjà écrit, de sorte que votre documentation se met à jour avec le travail au lieu de se périmer.",
    whatItIsIntro: "Autour de ce cycle de vie, wxKanban vous donne ce dont vous avez besoin pour mener un vrai travail en équipe :",
    teamMgmtLabel: "Gestion d'équipe",
    teamMgmtBody: "— invitez des personnes, définissez leurs rôles et contrôlez qui peut faire quoi.",
    checkoutLabel: "Check-out et check-in",
    checkoutBody: "— réservez un périmètre pour y travailler et restituez-le une fois terminé, afin que le travail reste coordonné et que personne n'écrase discrètement celui d'un autre.",
    cockpitLabel: "Le Cockpit",
    cockpitBody: "— un centre de commande en direct pour votre projet : où il en est, la suite, et chaque commande wxKanban au même endroit.",
    helpLabel: "Aide active et contextuelle",
    helpBody: "— des conseils et des réponses là où vous travaillez, à la fois dans wxKanban et dans VS Code, là où la construction se fait réellement.",
    whatItIsClosing: "C'est là le cœur de wxKanban : le cycle de vie piloté par les spécifications, avec les outils pour le mener en équipe.",
    whyHeading: "Pourquoi il existe",
    whyPara1: "L'IA a changé la façon dont les logiciels sont construits, mais le plus difficile n'a jamais été la syntaxe — c'était le ",
    whyEmphasis: "discernement",
    whyPara2: " pour mener une application jusqu'à sa livraison. wxKanban est conçu pour donner aux développeurs expérimentés un moyen de transporter ce discernement dans l'ère de l'IA, et pour offrir aux nouveaux venus cette discipline dès le premier jour — afin que l'IA accélère votre travail au lieu de vous échapper.",
    groundHeading: "Bâti sur un terrain qui vous appartient",
    groundPara: "Vos données résident dans une base de données SQL standard, votre authentification vous appartient et vous pouvez changer de fournisseur d'IA quand vous le souhaitez. Aucun verrouillage propriétaire, aucun fournisseur ne s'interpose entre vous et le logiciel dont vous dépendez.",
    behindHeading: "Qui est derrière",
    behindPara1: "wxKanban est créé par ",
    behindPara2: ", fondée par Andy « Cowboy » Stapleton — qui a passé plus de 40 ans à traverser chaque grand changement de plateforme, du mainframe au web puis au cloud, et à aider d'autres développeurs à franchir le même cap.",
    contactHeading: "Nous contacter",
    contactIntro: "Des questions ou des idées ? Écrivez à",
    contactOutro: "— nous répondons vraiment.",
    phoneLabel: "Téléphone :",
  },
  bioPage: {
    title: "À propos du fondateur",
    photoAlt: "Portrait du fondateur",
    loading: "Chargement…",
  },
  privacyPage: {
    title: "Politique de confidentialité",
    lastUpdated: "Dernière mise à jour : {{date}}",
    intro:
      "wxKanban (« nous », « wxperts ») respecte votre vie privée. La présente politique explique ce que nous collectons lorsque vous utilisez l'application wxKanban, pourquoi, et les choix dont vous disposez. Nous collectons ce dont nous avons besoin pour faire fonctionner le service, nous ne vendons pas vos données, et vous pouvez nous demander de les supprimer.",
    authoritativeNote:
      "La présente politique a été traduite à titre de commodité. La version anglaise fait foi.",
    sections: {
      s1heading: "Informations que nous collectons",
      s1item1:
        "Compte et coordonnées de l'entreprise — votre nom, votre adresse e-mail, votre mot de passe (stocké uniquement sous forme de hachage sécurisé), le nom de votre entreprise, votre rôle et votre préférence linguistique.",
      s1item2:
        "Le travail que vous créez — projets, tâches, spécifications, notes, saisies de temps, factures et contenus associés que vous saisissez dans l'application.",
      s1item3:
        "Informations de facturation — les détails d'abonnement et de paiement sont traités par notre prestataire de paiement, Stripe. Nous ne stockons pas les numéros de carte complets ; nous conservons votre forfait, votre statut et votre historique de facturation.",
      s1item4:
        "Services connectés — si vous reliez un outil de gestion de projet (par exemple Jira, Monday.com, Asana, Trello), nous stockons les jetons d'accès nécessaires à la synchronisation, chiffrés au repos.",
      s1item5:
        "Utilisation et journaux — journaux techniques de base (tels que les actions, horodatages et erreurs) utilisés pour exploiter, sécuriser et améliorer le service.",
      s2heading: "Comment nous les utilisons",
      s2item1:
        "Pour fournir et exploiter l'application — votre compte, vos projets, le suivi du temps et la facturation.",
      s2item2: "Pour traiter les abonnements et les paiements via Stripe.",
      s2item3:
        "Pour synchroniser avec les outils de gestion de projet que vous choisissez de connecter.",
      s2item4: "Pour alimenter les fonctionnalités assistées par IA (voir ci-dessous).",
      s2item5: "Pour sécuriser le service, prévenir les abus et fournir de l'assistance.",
      s3heading: "Traitement par IA",
      s3body:
        "Certaines fonctionnalités envoient le contenu que vous fournissez (par exemple, des spécifications ou du texte associé) à des fournisseurs d'IA tiers — tels que Google Gemini, OpenAI ou d'autres que nous configurons — uniquement pour générer le résultat demandé. Nous n'utilisons pas votre contenu pour entraîner nos propres modèles, et les fournisseurs d'IA le traitent selon leurs propres conditions en tant que nos prestataires de services.",
      s4heading: "Avec qui nous les partageons",
      s4body:
        "Nous ne vendons ni ne louons vos informations. Nous ne les partageons qu'avec les prestataires de services qui nous aident à faire fonctionner wxKanban — par exemple, l'hébergement cloud (Amazon Web Services), le traitement des paiements (Stripe) et les fournisseurs d'IA mentionnés ci-dessus — et uniquement pour qu'ils puissent réaliser ces services pour nous. Nous pouvons divulguer des informations si la loi l'exige.",
      s5heading: "Sécurité",
      s5body:
        "Nous protégeons vos données par un chiffrement en transit et au repos, des mots de passe hachés et des contrôles d'accès. Aucun système n'est parfaitement sécurisé, mais nous travaillons en permanence à protéger vos informations.",
      s6heading: "Durée de conservation",
      s6body:
        "Nous conservons votre compte et le contenu que vous créez tant que votre compte est actif, et pendant une période raisonnable par la suite selon les besoins à des fins juridiques, comptables et opérationnelles. Vous pouvez nous demander de supprimer vos données à tout moment.",
      s7heading: "Vos choix et vos droits",
      s7body:
        "Vous pouvez accéder à vos informations personnelles, les corriger, les exporter ou les supprimer, et retirer votre consentement le cas échéant. Si vous vous trouvez dans une région dotée de droits spécifiques en matière de protection des données (comme l'UE ou le Royaume-Uni sous le GDPR, ou les lois d'État américaines sur la confidentialité), ces droits s'appliquent à vous. Pour les exercer, écrivez à",
      s8heading: "Transferts internationaux",
      s8body:
        "wxKanban est exploité depuis les États-Unis, et vos informations peuvent y être traitées. En utilisant le service, vous consentez à ce transfert, sous réserve des protections décrites dans la présente politique.",
      s9heading: "Contact",
      s9body: "Des questions sur la confidentialité ? Écrivez à",
    },
  },
  termsPage: {
    title: "Conditions d'utilisation",
    lastUpdated: "Dernière mise à jour : {{date}}",
    intro:
      "Les présentes conditions régissent votre utilisation de l'application et du site web wxKanban. En créant un compte ou en utilisant le service, vous les acceptez. Veuillez également consulter notre",
    authoritativeNote:
      "Les présentes conditions ont été traduites à titre de commodité. La version anglaise fait foi.",
    sections: {
      s1heading: "Votre compte",
      s1body:
        "Vous êtes responsable de l'exactitude des détails de votre compte et de la sécurité de vos identifiants. Vous êtes responsable de l'activité qui se déroule sous votre compte, ainsi que des membres de l'équipe que vous invitez.",
      s2heading: "Utilisation acceptable",
      s2body:
        "Utilisez wxKanban de manière licite. N'essayez pas de perturber le service ou d'y accéder sans autorisation, de détourner les données d'autres utilisateurs, ni d'utiliser la plateforme pour créer ou stocker du contenu illégal.",
      s3heading: "Abonnements et facturation",
      s3body:
        "Les forfaits payants sont facturés via Stripe de manière récurrente jusqu'à leur annulation. Les frais sont facturés à l'avance pour chaque période de facturation et ne sont pas remboursables, sauf lorsque la loi l'exige. Vous pouvez annuler à tout moment ; votre forfait reste actif jusqu'à la fin de la période en cours. Nous vous informerons dans un délai raisonnable de tout changement de prix.",
      s4heading: "Votre contenu et sa propriété",
      s4body:
        "Vous êtes propriétaire des données et du contenu que vous créez dans wxKanban. Vous nous accordez la licence limitée nécessaire pour les héberger, les traiter, les sauvegarder et les afficher afin que nous puissions fournir le service. Vous pouvez exporter ou supprimer votre contenu comme décrit dans notre Politique de confidentialité.",
      s5heading: "Résultats générés par l'IA",
      s5body:
        "wxKanban utilise l'IA pour vous assister dans votre travail. Les résultats de l'IA peuvent être imparfaits — il vous incombe de vérifier, tester et approuver tout ce qui est généré avant de vous y fier. Nous ne garantissons pas que les résultats assistés par IA soient exacts, complets ou adaptés à un usage particulier.",
      s6heading: "Notre propriété intellectuelle",
      s6body:
        "L'application wxKanban, sa conception ainsi que les noms wxKanban et wxperts appartiennent à wxperts ou à ses concédants de licence. Les présentes conditions ne vous accordent aucun droit sur notre logiciel ou notre image de marque au-delà de l'utilisation du service tel que prévu.",
      s7heading: "Absence de garanties",
      s7body:
        "Le service est fourni « en l'état » et « selon disponibilité », sans garantie d'aucune sorte. Nous nous efforçons de maintenir wxKanban fiable et disponible, mais ne garantissons pas un fonctionnement ininterrompu ou exempt d'erreurs.",
      s8heading: "Limitation de responsabilité",
      s8body:
        "Dans toute la mesure permise par la loi, wxperts n'est pas responsable des pertes indirectes, accessoires ou consécutives, ni de la perte de données ou de profits, découlant de votre utilisation — ou de votre incapacité à utiliser — le service.",
      s9heading: "Résiliation",
      s9body:
        "Vous pouvez fermer votre compte à tout moment. Nous pouvons suspendre ou résilier l'accès en cas de violation des présentes conditions ou pour protéger le service. En cas de résiliation, les dispositions qui, par leur nature, doivent survivre continueront de s'appliquer.",
      s10heading: "Droit applicable",
      s10body:
        "wxperts est basé aux États-Unis, et les présentes conditions sont régies par le droit américain, sans égard aux principes de conflit de lois. Si vous utilisez le service depuis l'Union européenne, rien dans les présentes conditions ne supprime les droits impératifs en matière de protection des consommateurs et des données dont vous bénéficiez en vertu du droit de l'UE (y compris le GDPR) ; ces protections continuent de s'appliquer à vous.",
      s11heading: "Modifications",
      s11body:
        "Nous pouvons mettre à jour les présentes conditions à mesure que le produit évolue. La date de « dernière mise à jour » ci-dessus indique la date de leur dernier changement ; toute utilisation continue après une modification signifie que vous l'acceptez.",
      s12heading: "Contact",
      s12body: "Des questions sur ces conditions ? Écrivez à",
    },
  },
  adminDashboard: {
    saveFailed: "Échec de l'enregistrement",
    editAuditType: "Modifier le type d'audit",
    complianceFramework: "Cadre de conformité",
    none: "— Aucun —",
    title: "Tableau de bord administrateur",
    welcomeBack: "Bon retour, {{email}}",
    teamMembers: "Membres de l'équipe",
    activeProjects: "Projets actifs",
    hoursThisWeek: "Heures cette semaine",
    timeBilling: "Temps et facturation",
    timeBillingDesc: "Suivez le temps, définissez les taux de facturation et gérez les heures facturables sur tous les projets.",
    timeBillingPlaceholder: "Les saisies de temps et les taux de facturation apparaîtront ici.",
    systemHealth: "État du système",
    reportsAnalytics: "Rapports et analyses",
    reportsDesc: "Résumés des revenus, heures par projet, rentabilité et rapports exportables.",
    reports: "Rapports",
    reportsPlaceholder: "Les rapports financiers apparaîtront ici une fois les saisies de temps enregistrées.",
  },
  subscriptionCard: {
    statusActive: "Actif",
    statusPending: "En attente",
    statusSuspended: "Suspendu",
    currentPlan: "Forfait actuel",
    teamSeats: "Sièges d'équipe",
    seatLimitReached: "Limite de sièges atteinte — passez à un forfait supérieur pour inviter plus de membres.",
    upgradePlan: "Améliorer le forfait",
  },
  engagementsWidget: {
    title: "Engagements",
    clientFirms: "{{count}} cabinet de conseil sur vos projets",
    clientFirmsPlural: "{{count}} cabinets de conseil sur vos projets",
    clientsServed: "{{count}} client que vous servez",
    clientsServedPlural: "{{count}} clients que vous servez",
    viewAll: "Voir tout",
    roleHiring: "Recrutement",
    roleServing: "Prestation",
    moreCount: "+ {{count}} de plus",
  },
  activityFeed: {
    title: "Fil d'activité",
    loading: "Chargement de l'activité récente...",
    subtitle: "Activité récente de l'équipe et des projets",
    viewAll: "Voir tout",
    empty: "Aucune activité récente",
    emptyHint: "L'activité apparaîtra ici au fur et à mesure du travail de votre équipe",
    byUser: "par {{name}}",
    justNow: "à l'instant",
    minutesAgo: "il y a {{count}} min",
    hoursAgo: "il y a {{count}} h",
    daysAgo: "il y a {{count}} j",
  },
  dailyDigest: {
    title: "Résumé quotidien",
    loadingActivity: "Chargement de l'activité du jour...",
    subtitle: "Résumé de l'activité de l'équipe aujourd'hui",
    configure: "Configurer",
    viewAll: "Tout afficher",
    noActivity: "Aucune activité pour le moment aujourd'hui",
    activityWillAppear: "L'activité de l'équipe apparaîtra ici",
    byUser: "par {{name}}",
    moreItems: "+{{count}} autres éléments",
    justNow: "à l'instant",
    minutesAgo: "il y a {{count}} min",
    hoursAgo: "il y a {{count}} h",
  },
  roleLock: {
    contactAdmin: "Contactez votre administrateur pour accéder à cette section.",
    requiresAdmin: "Nécessite un administrateur",
  },
  upgradeLock: {
    requiresPlan: "Nécessite le forfait {{plan}}",
    upgradePlan: "Mettre à niveau le forfait",
  },
  projectProposals: {
    errorFindDraft: "Impossible de trouver le brouillon d'aperçu pour cette proposition",
    errorOpenEditor: "Échec de l'ouverture de l'éditeur",
    errorFetch: "Échec de la récupération des propositions : {{status}}",
    errorLoad: "Échec du chargement des propositions",
    errorUpdateStatus: "Échec de la mise à jour du statut",
    rejectionReasonPrompt: "Motif du rejet (obligatoire) :",
    title: "Propositions de projet",
    addProposal: "Ajouter une proposition",
    loadingProposals: "Chargement des propositions...",
    empty: "Aucune proposition pour l'instant. Ajoutez-en une pour commencer.",
    status_pending: "En attente",
    status_good_idea: "Bonne idée",
    status_accepted: "Acceptée",
    status_rejected: "Rejetée",
    editTitle: "Ouvrir l'éditeur d'aperçu pour cette proposition",
    goodIdea: "Bonne idée",
    reject: "Rejeter",
    accept: "Accepter",
    createProject: "Créer un projet",
    reopen: "Rouvrir",
    projectCreated: "Projet créé",
  },
  systemHealth: {
    title: "État du système",
    checked: "Vérifié à {{time}}",
    refresh: "Actualiser",
    runningDiagnostics: "Diagnostics en cours…",
    allSystemsOperational: "Tous les systèmes sont opérationnels",
    driftOrMissingDetected: "Dérive de schéma ou tables manquantes détectées",
    databaseUnreachable: "Base de données inaccessible",
    database: "Base de données",
    status: "État",
    latency: "Latence",
    errorLabel: "Erreur",
    tableRows: "lignes {{table}}",
    server: "Serveur",
    uptime: "Disponibilité",
    memory: "Mémoire",
    env: "Env",
    diagTime: "Temps de diag",
    pmIntegrationsConnected: "intégrations PM connectées",
    pmIntegrationConnected: "intégration PM connectée",
    schemaDriftTables: "Dérive de schéma — {{count}} tables",
    schemaDriftTable: "Dérive de schéma — {{count}} table",
    missing: "manquant",
    run: "Exécutez",
    toFix: "pour corriger.",
    missingTables: "Tables manquantes ({{count}})",
    toCreateMissingTables: "pour créer les tables manquantes.",
  },
  projectGrid: {
    opening: "Ouverture…",
    lastUpdated: "Dernière mise à jour : {{date}}",
    notCreated: "non créé",
    noAudit: "Aucun audit",
    editAuditType: "Modifier le type d'audit",
    outstandingTasks: "Tâches en attente",
    complete: "Terminé",
    inProgress: "En cours",
    taskProgress: "{{total}} tâches - {{completed}} terminées",
    notStarted: "Non démarré",
    failedOpenOverview: "Échec de l'ouverture de l'éditeur d'aperçu",
    failedOpenOverviewStatus: "Échec de l'ouverture de l'éditeur d'aperçu ({{status}})",
    titleProjectOverview: "Aperçu du projet",
    titleProjectLifecycle: "Lifecycle du projet",
    titleDevelopmentPlan: "Plan de développement",
    titleAuditReport: "Rapport d'audit",
    titleCompoundLearnings: "Enseignements cumulés",
    titleProjectScopeAnalysis: "Analyse de Scope du projet",
    titleEntityDiagram: "Diagramme d'entités",
    showDocuments: "Afficher les documents",
    hideDocuments: "Masquer les documents",
    newProject: "Nouveau",
    allProjects: "Tous les projets",
    active: "Actifs",
    branched: "Dérivés",
    emptyAdmin: "Aucun projet actif pour le moment. Cliquez sur Nouveau pour créer votre premier projet.",
    emptyTeam: "Aucun projet ne vous est encore attribué.",
  },
  phaseTabBar: {
    assignedUser: "Utilisateur assigné",
  },
  designPhase: {
    specifications: "Spécifications",
    clarifiedCount: "{{clarified}} / {{total}} clarifiées",
  },
  implementationPhase: {
    tasksHeading: "Tâches",
    openCount: "{{n}} ouverte(s)",
  },
  regression: {
    loading: "Comparaison avec les exécutions précédentes…",
    failed: "Impossible de construire la comparaison",
    title: "Contrôle de régression pour {{version}}",
    blocking: "{{count}} bloquant(s)",
    newlyFailing: "Nouvellement en échec",
    newlyFailingHint: "Réussi lors de sa dernière exécution. C'est ce qui bloque la livraison.",
    neverPassed: "Jamais réussi",
    neverPassedHint: "Travail en cours, pas une régression.",
    stillPassing: "Toujours réussi",
    lastPassed: "Dernière réussite en {{version}}",
  },
  testSuite: {
    skipTitle: "Exigence de tests",
    skipOn: "Les tests sont DÉSACTIVÉS pour ce projet.",
    skipOff: "Les tests sont obligatoires pour ce projet.",
    skipTurnOn: "Ignorer les tests",
    skipReEnable: "Exiger à nouveau les tests",
    skipCancel: "Annuler",
    skipConfirm: "Cela désactive la porte de livraison pour TOUS les périmètres de ce projet. Les tests obligatoires ne bloqueront plus, et les livraisons seront marquées comme ignorées plutôt que testées. Ceci est enregistré à votre nom.",
    skipConfirmYes: "Oui, ignorer les tests",
    skipHint: "Administrateurs uniquement. La modification est inscrite au journal d'audit.",
    skipBannerTitle: "Les tests sont désactivés pour ce projet",
    skipBannerBody: "Les tests obligatoires ne bloquent pas les livraisons. Les portes indiquent « ignorée », pas « réussie ».",
    scopeFilter: "Périmètre :",
    allScopes: "Tous les périmètres ({{count}})",
    loading: "Chargement de la suite de tests…",
    loadFailed: "Impossible de charger la suite de tests",
    runFailed: "Impossible de lancer l'exécution",
    empty: "Aucun test pour l'instant. Ils sont générés lors de l'implémentation du périmètre.",
    cycle: "Version {{version}}",
    cycleStatus: { open: "En développement", uat: "En UAT", production: "En production", superseded: "Remplacée" },
    humanPending: "{{count}} test(s) utilisateur en attente de mise à disposition en UAT",
    runGate: "Lancer {{gate}}",
    noItemsForGate: "Aucun test dans la porte {{gate}}",
    runRunning: "Exécution en cours",
    runStalled: "Exécution bloquée — aucune réponse de l'exécuteur",
    runTotals: "{{passed}} réussis · {{failed}} échoués · {{total}} au total",
    required: "Obligatoire",
    origin: { ai: "IA", user: "Testeur" },
    inherit: "Hérite", override: "Remplacer",
    forceInherited: "Hérite du caractère obligatoire du périmètre",
    forceOverridden: "Remplace le caractère obligatoire du périmètre",
    forceScopeTitle: "Test obligatoire",
    forceScopeLabel: "Exiger la validation des {{count}} tests de ce périmètre",
    forceScopeHint: "Si activé, le produit ne peut pas avancer vers la production sans validation de ces tests.",
    filter: { all: "Tous", outstanding: "En attente", forced: "Obligatoires", failing: "En échec" },
  },
  userTest: {
    loading: "Chargement de vos tests…",
    loadFailed: "Impossible de charger vos tests",
    signoffFailed: "Enregistrement impossible",
    dismissFailed: "Impossible d'écarter le nouveau test",
    notReleasedTitle: "Pas encore prêt pour vous",
    notReleasedBody: "{{count}} test(s) en attente. Ils sont mis à disposition dès que les tests automatiques de cette version passent.",
    summary: "{{required}} obligatoires · {{optional}} optionnels · version {{version}}",
    required: "Obligatoire", optional: "Optionnel",
    testerAuthored: "Ajouté par un testeur",
    addTest: "Ajouter un test",
    preconditions: "Avant de commencer", steps: "Étapes", postconditions: "Vérifier ensuite",
    action: "Faites ceci", expected: "Vous devriez voir",
    pass: "Réussi", fail: "Échoué",
    notesPlaceholder: "Que s'est-il passé ? (obligatoire en cas d'échec)",
    failNeedsReason: "Dites ce qui s'est réellement passé — cela devient le rapport d'anomalie.",
    failHint: "Un échec crée un rapport d'anomalie auquel ces étapes sont jointes.",
    bugFiled: "Rapport d'anomalie créé avec vos étapes et vos observations.",
    viewBug: "Ouvrir le rapport d'anomalie",
    retestTitle: "Merci de retester",
    retestImpact: "Le code couvert par ce test a changé : {{path}} (à {{hops}} étape(s)).",
    retestBroad: "Un changement de grande portée est arrivé ; tous les tests utilisateur sont donc revérifiés.",
    retestUndetermined: "Nous n'avons pas pu déterminer si le changement touche ce test, d'où la demande.",
    dismissRetest: "Non concerné",
    dismissExplain: "Si le changement ne peut pas affecter ce que vous avez vérifié, expliquez pourquoi. Votre validation précédente reste valable.",
    dismissPlaceholder: "Pourquoi ce changement n'affecte pas ce test",
    confirmDismiss: "Écarter",
  },
  addTest: {
    title: "Ajouter un test à partir de votre observation",
    intro: "Vous avez trouvé quelque chose que la spécification n'avait pas prévu. Notez-le pour qu'il soit vérifié à chaque version.",
    whatHappened: "Ce qui devrait être vrai",
    titlePlaceholder: "ex. Changer de phase avec un minuteur actif conserve la durée correcte",
    setup: "Préparation (une par ligne)",
    setupPlaceholder: "ex. Un projet avec un minuteur en cours",
    steps: "Étapes",
    actionPlaceholder: "Faites ceci…",
    expectedPlaceholder: "Vous devriez voir… (une valeur, un nombre, un message)",
    addStep: "Ajouter une étape",
    after: "Vérifier ensuite (une par ligne)",
    afterPlaceholder: "ex. Le total des temps saisis est inchangé",
    vagueWarning: "L'étape ou les étapes {{steps}} disent en substance \"fonctionne\". Précisez ce qui doit être visible : une valeur, un nombre ou un message.",
    needTitle: "Donnez un titre au test",
    needStep: "Ajoutez au moins une étape",
    needExpected: "Chaque étape a besoin d'un résultat attendu",
    failed: "Impossible d'enregistrer le test",
    cancel: "Annuler", save: "Enregistrer le test",
  },
  qaPhase: {
    testTasks: "Tâches de test",
    passCount: "{{n}} réussi",
    failCount: "{{n}} échoué",
  },
  humanTestingPhase: {
    feedbackItems: "Éléments de retour",
    issuesRaisedByTesters: "Problèmes signalés par les testeurs humains",
    openCount: "{{count}} ouvert(s)",
  },
  betaPhase: {
    approvalTitle: "Phase Beta — Approbation d'un administrateur requise",
    approvalBody: "La création de nouvelles tâches pendant la phase Beta nécessite l'approbation explicite d'un administrateur. Cela sera consigné dans la piste d'audit.",
    approveAndCreate: "Approuver et créer",
    feedbackTitle: "Retours Beta",
    feedbackHint: "Les modifications nécessitent l'approbation d'un administrateur",
    openCount: "{{count}} ouvert(s)",
    creatingApprovedTask: "Création de la tâche approuvée...",
  },
  releasePhase: {
    statusComplete: "Terminé",
    statusInProgress: "En cours",
    statusReopened: "Rouvert",
    statusPending: "En attente",
    manualDeployment: "Déploiement manuel",
    ciCdPipeline: "Pipeline CI/CD",
    stagedRollout: "Déploiement progressif",
    hotfix: "Correctif à chaud",
    releaseRecord: "Fiche de version",
    priorPhasesIncomplete: "Phases précédentes incomplètes",
    versionNumber: "Numéro de version",
    versionPlaceholder: "ex. v1.0.0",
    releaseDate: "Date de version",
    releaseMethod: "Méthode de version",
    releaseNotes: "Notes de version",
    notesPlaceholder: "Résumez ce qui a été publié, les changements clés, les problèmes connus...",
    saved: "Enregistré",
    saveNotes: "Enregistrer les notes",
    fullLifecycleSummary: "Résumé complet du cycle de vie",
    columnPhase: "Phase",
    columnStatus: "Statut",
    columnEntered: "Commencée",
    columnCompleted: "Terminée",
    projectLabel: "Projet",
  },
  phaseCompletionBar: {
    advanceFailed: "Échec de l'avancement de la phase",
    reopenFailed: "Échec de la réouverture de la phase",
    checkingCriteria: "Vérification des critères d'achèvement...",
    phaseComplete: "Phase terminée",
    phaseReopened: "Phase rouverte",
    readyToAdvance: "Prêt à avancer",
    criteriaMet: "{{pass}} critères sur {{total}} satisfaits",
    collapse: "Réduire",
    expand: "Développer",
    advancing: "Avancement...",
    markAsReleased: "Marquer comme publié",
    advancePhase: "Avancer la phase",
    reopenedForRevisions: "Cette phase a été rouverte pour révisions.",
    cancelReopen: "Annuler la réouverture",
    reopenPhase: "Rouvrir la phase",
    reopenReasonPlaceholder: "Motif de la réouverture de cette phase...",
    reopening: "Réouverture...",
    confirmReopen: "Confirmer la réouverture",
  },
  phaseTaskGroup: {
    unassigned: "Non assigné",
    unnamedScope: "Scope sans nom",
    noTasksInScope: "Aucune tâche dans ce scope.",
    noTasksInPhase: "Aucune tâche dans cette phase.",
    unscopedTasks: "Tâches sans scope",
    failedToLoad: "Échec du chargement des tâches",
    requestFailed: "Échec de la requête : {{status}}",
  },
  taskList: {
    statusOpen: "Ouvert",
    statusInProgress: "En cours",
    statusCompleted: "Terminé",
    statusDismissed: "Rejeté",
    resultNone: "—",
    resultPass: "Réussi",
    resultFail: "Échoué",
    resultBlocked: "Bloqué",
    priorityLow: "Basse",
    priorityMed: "Moy.",
    priorityHigh: "Haute",
    priorityCrit: "Crit.",
    taskNamePlaceholder: "Nom de la tâche...",
    descriptionPlaceholder: "Description (facultative)...",
    addTaskButton: "Ajouter la tâche",
    addTask: "Ajouter une tâche",
    createError: "Échec de la création de la tâche",
    noTasks: "Aucune tâche pour le moment",
    colTask: "Tâche",
    colResult: "Résultat",
    colRaisedBy: "Signalé par",
    colPriority: "Priorité",
    colStatus: "Statut",
    countOpen: "{{n}} ouvertes",
    countClosed: "{{n}} fermées",
    countTotal: "{{n}} au total",
  },
  phase: {
    design: "Conception",
    implementation: "Implémentation",
    qa: "QA",
    humanTesting: "Tests humains",
    beta: "Bêta",
    release: "Publication",
  },
  projectPanel: {
    saveFailed: "Échec de l'enregistrement",
    failedToLoad: "Échec du chargement",
    loadingTasks: "Chargement des tâches…",
    noTasksForScope: "Aucune tâche pour ce scope.",
    filterAll: "toutes",
    filterOpen: "ouvertes",
    filterClosed: "fermées",
    noFilterTasks: "Aucune tâche {{filter}}.",
    openTasks: "Tâches ouvertes",
    noOpenTasks: "Aucune tâche ouverte.",
    statTotal: "Total",
    statOpen: "Ouvert",
    statClosed: "Fermé",
    statInProgress: "En cours",
    statComplete: "Terminé",
    statActive: "Actif",
    statResolved: "Résolu",
    statScopesPassing: "Scopes réussis",
    statQaTasksDone: "Tâches QA faites",
    statNoTasks: "Aucune tâche",
    documents: "Documents",
    completeCheck: "✓ terminé",
    tasksCount: "Tâches ({{done}}/{{total}})",
    specOpenClosed: "Spec {{num}} : Ouvertes {{open}} · Fermées {{closed}}",
    // [SCOPE 137 / T014] Change Impact agreement log (read-only)
    changeImpactTitle: "Journal Change Impact",
    changeImpactCount: "Journal Change Impact ({{count}})",
    changeImpactLoading: "Chargement…",
    changeImpactEmpty: "Aucun accord ni franchissement n’a été enregistré pour ce scope.",
    changeImpactError: "Le journal Change Impact n’a pas pu être chargé.",
    changeImpactKindAck: "Pris en compte",
    changeImpactKindApproval: "Approuvé",
    changeImpactKindDeclined: "Refusé",
    changeImpactKindCrossing: "Franchi sans supervision",
    changeImpactAffected: "{{count}} concerné(s)",
    changeImpactQueued: "envoyé après une période hors ligne",
    changeImpactReadOnly: "Lecture seule. Ces enregistrements ne peuvent pas être modifiés.",
    reopen: "Rouvrir",
    reactivate: "Réactiver",
    markClarified: "Marquer clarifié",
    defer: "Reporter",
    needsClarification: "Clarification requise",
    deleteScope: "Supprimer",
    deleteScopeConfirm: "Supprimer définitivement le scope {{num}} « {{title}} » et ses {{tasks}} tâche(s) et {{docs}} document(s) ? Cette action est irréversible.",
    deleteScopeError: "Impossible de supprimer le scope. Veuillez réessayer.",
    overviewDoc: "Doc d'aperçu",
    scopeCountOne: "{{n}} scope",
    scopeCountOther: "{{n}} scopes",
    fullyGenerated: "{{n}} entièrement générés",
    noScopesCreatedYet: "Aucun scope créé pour le moment.",
    scopeDocuments: "Documents de scope",
    noScopes: "Aucun scope.",
    progressByScope: "Progression par scope",
    qaStatusByScope: "Statut QA par scope",
    allTestsPassed: "Les {{n}} tests réussis",
    inProgressDone: "{{n}} en cours · {{done}}/{{total}} faits",
    completeCount: "{{done}}/{{total}} terminés",
    noQaTasks: "Aucune tâche QA",
    betaApprovalNotice: "Les nouvelles tâches en Bêta nécessitent l'approbation d'un ADMIN avant leur création.",
    feedbackByScope: "Retours par scope",
    allScopesClear: "Tous les scopes sont clairs — prêt pour la publication.",
    openItemsRemain: "{{n}} éléments ouverts subsistent dans les scopes.",
    phaseChecklist: "Liste de contrôle des phases",
    openItemsByScope: "Éléments ouverts par scope",
    openResolved: "{{open}} ouverts · {{done}}/{{total}} résolus",
    phaseView: "Vue {{phase}}",
    allProjects: "Tous les projets",
    open: "Ouvrir",
    status: {
      draft: "Brouillon",
      inReview: "En révision",
      pending: "En attente",
      clarified: "Clarifié",
      planned: "Planifié",
      tasksGenerated: "Tâches générées",
      implemented: "Implémenté",
      qaPassed: "QA réussie",
      humanTested: "Testé par humain",
      betaCleared: "Bêta validée",
      released: "Publié",
      deferred: "Reporté",
    },
    doctype: {
      tasks: "Tâches",
      datamodel: "Modèle de données",
      quickstart: "Démarrage rapide",
      checklist: "Liste de contrôle",
      contracts: "Contrats",
      research: "Recherche",
      overview: "Aperçu",
      stackStyle: "Stack et style",
    },
    issuetype: {
      bug: "Bogue",
      feature: "Fonctionnalité",
      enhancement: "Amélioration",
      task: "Tâche",
    },
  },
  helpCatalog: {
    ui: {
      included: "Inclus",
      upgrade: "Mise à niveau",
      admin: "Admin",
      adminOnly: "Administrateur uniquement",
      howToLabel: "Comment faire :",
      howToUseIt: "Comment l'utiliser",
      availableFromPrefix: "Disponible à partir du forfait ",
      availableFromSuffix: ".",
      yourPlan: "Votre forfait",
      subscriptionPlan: "Forfait d'abonnement",
    },
    category: {
      coreWorkspace: "Espace de travail principal",
      developmentLifecycle: "Cycle de vie du développement",
      integrations: "Intégrations",
      teamCollaboration: "Équipe et collaboration",
      timeBillingConsulting: "Temps, facturation et conseil",
      adminConfiguration: "Administration et configuration",
      securityCompliance: "Sécurité et conformité",
      enterpriseSupport: "Entreprise et support",
    },
    plan: {
      SINGLE_USER: {
        label: "Utilisateur unique",
        userLimit: "1 utilisateur",
      },
      SMALL_BUSINESS: {
        label: "Petite/Moyenne entreprise",
        userLimit: "Jusqu'à 5 utilisateurs",
      },
      CONSULTANT: {
        label: "Consultant",
        userLimit: "Équipe illimitée",
      },
      ENTERPRISE: {
        label: "Entreprise",
        userLimit: "25 utilisateurs et plus",
      },
    },
    feature: {
      dashboard: {
        title: "Tableau de bord",
        description: "Votre vue d'ensemble des projets — la progression des phases du Lifecycle pour chaque projet, ainsi que des liens rapides vers les documents générés de chaque projet.",
        howTo: "Ouvrez le Dashboard depuis la barre latérale. Chaque fiche de projet affiche sa progression par phase et ses liens vers les documents ; les ADMIN disposent d'un bouton Nouveau pour créer un projet directement.",
      },
      projects: {
        title: "Projets",
        description: "Créez et gérez des projets. Nombre de projets illimité sur tous les forfaits payants.",
        howTo: "Créez un projet via le bouton Nouveau du tableau de bord ou le menu Projets, puis ouvrez-le pour voir ses onglets (aperçu, lifecycle, tâches).",
      },
      tasks: {
        title: "Mes tâches (Kanban)",
        description: "Le travail qui vous est assigné, présenté sur un tableau Kanban couvrant tous vos projets.",
        howTo: "Ouvrez Mes tâches ; faites glisser les cartes entre les colonnes pour changer leur statut, ou ouvrez une carte pour plus de détails.",
      },
      settings: {
        title: "Paramètres",
        description: "Paramètres de compte, d'entreprise et de préférences, y compris la langue.",
        howTo: "Ouvrez Paramètres depuis le menu ; le sélecteur de langue et le profil s'y trouvent.",
      },
      faq: {
        title: "FAQ",
        description: "Réponses aux questions courantes sur l'utilisation de wxKanban.",
        howTo: "Ouvrez la FAQ depuis le menu.",
      },
      lifecycle: {
        title: "Lifecycle en 6 phases et orchestrateur",
        description: "La méthodologie Design → Implementation → QA → HumanTesting → Beta → Release qui encadre chaque fonctionnalité et maintient une source de vérité unique sur l'état d'avancement d'un projet.",
        howTo: "Pilotez-le depuis Claude Code + le kit wxKanban dans VS Code (buildscope → createSpecs → implement). La bonne commande n'est disponible qu'à la bonne phase.",
      },
      specs: {
        title: "Specs et Scopes",
        description: "Les documents Scope-of-project ainsi que les Specs, plans et tâches générés pour chaque fonctionnalité.",
        howTo: "Rédigez un Scope avec buildscope ; générez la Spec, le plan et les tâches avec createSpecs ; suivez-les dans le Cockpit.",
      },
      audit: {
        title: "Rapports d'audit",
        description: "Une piste d'audit par release retraçant ce qui a été construit, testé et encadré par des balises de code.",
        howTo: "Ouvrez le lien Rapport d'audit sur une fiche de projet, ou régénérez-le depuis le tableau de bord Admin.",
      },
      cockpit: {
        title: "Dev Cockpit",
        description: "Une barre latérale VS Code affichant le travail restant par Scope, en temps réel à mesure que le travail avance.",
        howTo: "Installez l'extension Dev Cockpit ; elle épingle votre Scope actif et liste les tâches non terminées.",
      },
      pmIntegrations: {
        title: "Intégrations PM",
        description: "Connectez Jira, Monday.com, Asana et Trello et synchronisez les tâches dans les deux sens.",
        howTo: "Ouvrez Intégrations PM et connectez-vous via OAuth ; la synchronisation s'exécute selon une planification en arrière-plan.",
      },
      team: {
        title: "Gestion de l'équipe",
        description: "Invitez des coéquipiers, attribuez des rôles et gérez les accès grâce à des contrôles basés sur les rôles.",
        howTo: "Ouvrez Équipe ; invitez par e-mail et définissez le rôle de chaque membre.",
      },
      timeTracking: {
        title: "Suivi du temps et facturation",
        description: "Minuteur intégré avec suivi des heures facturables et saisies de temps.",
        howTo: "Démarrez le minuteur sur une tâche ; marquez les saisies comme facturables pour la facturation.",
      },
      invoices: {
        title: "Factures",
        description: "Générez des factures PDF à partir des saisies de temps facturables.",
        howTo: "Ouvrez Factures, choisissez un projet et une plage de dates, puis générez ; les saisies facturées sont marquées pour éviter les doublons.",
      },
      reports: {
        title: "Rapports",
        description: "Rapports avancés couvrant le temps, les projets et les résultats.",
        howTo: "Ouvrez Rapports depuis le menu.",
      },
      workHub: {
        title: "Work Hub",
        description: "Un centre de gestion des missions pour piloter le travail réalisé pour les clients.",
        howTo: "Ouvrez le Work Hub pour consulter et gérer vos missions.",
      },
      clientPortal: {
        title: "Portail client",
        description: "Offrez aux clients une vue restreinte et en lecture seule de leur projet.",
        howTo: "Activez le portail pour un projet et partagez le lien client.",
      },
      customWorkflows: {
        title: "Flux de travail personnalisés",
        description: "Adaptez les flux de travail du Lifecycle au processus de votre équipe.",
        howTo: "Configurez des flux de travail personnalisés dans les paramètres du projet.",
      },
      apiAccess: {
        title: "Accès API",
        description: "Accès programmatique à vos données wxKanban via API.",
        howTo: "Créez un jeton API dans les Paramètres et appelez les points de terminaison documentés.",
      },
      stackProfiles: {
        title: "Profils de Stack",
        description: "Définitions réutilisables de Stack cible et de style appliquées lors de la création d'un projet.",
        howTo: "Ouvrez les Profils de Stack (Admin) pour définir ou modifier les stacks de votre entreprise.",
      },
      themeProfiles: {
        title: "Profils de thème",
        description: "Thèmes d'apparence réutilisables pour les sorties générées.",
        howTo: "Ouvrez les Profils de thème (Admin) pour définir ou modifier des thèmes.",
      },
      encryption: {
        title: "Chiffrement au repos",
        description: "Les secrets sont chiffrés au repos avec AES-256-GCM.",
        howTo: "Toujours actif — aucune configuration requise.",
      },
      soc2: {
        title: "SOC 2 et HIPAA",
        description: "Contrôles et outils de conformité alignés sur SOC 2 et HIPAA.",
        howTo: "Ajoutez les documents SDLC de votre entreprise pour l'analyse de conformité ; attribuez des contrôles aux membres de l'équipe.",
      },
      sso: {
        title: "Authentification unique (SSO)",
        description: "SSO d'entreprise pour votre organisation.",
        howTo: "Contactez votre représentant wxKanban pour configurer votre fournisseur d'identité.",
      },
      advancedSecurity: {
        title: "Sécurité avancée",
        description: "Contrôles de sécurité supplémentaires de niveau entreprise.",
        howTo: "Configuré dans le cadre de l'intégration Enterprise.",
      },
      emailSupport: {
        title: "Assistance par e-mail",
        description: "Assistance produit par e-mail.",
        howTo: "Envoyez un e-mail à admin@wxperts.com.",
      },
      dedicatedSupport: {
        title: "Assistance dédiée",
        description: "Un contact d'assistance dédié pour votre compte.",
        howTo: "Attribué lors de l'intégration Enterprise.",
      },
      customIntegrations: {
        title: "Intégrations personnalisées",
        description: "Intégrations sur mesure conçues pour votre organisation.",
        howTo: "Définissez le périmètre des intégrations personnalisées avec votre représentant wxKanban.",
      },
      sla: {
        title: "Garantie SLA",
        description: "Un SLA contractuel sur la disponibilité et les délais de réponse.",
        howTo: "Inclus dans votre contrat Enterprise.",
      },
    },
  },
  // [SCOPE 052] Pages Aide/Tutoriels publiques (/docs, /docs/:slug)
  docs: {
    title: "Aide/Tutoriels",
    backHome: "← Retour à l'accueil",
    sectionsLabel: "Sections d'aide",
    navFaq: "FAQ",
    navVideos: "Vidéos",
    navSessions: "Sessions de formation",
    navGuides: "Guides",
    guidesHeading: "Guides",
    guidesSubtitle: "Des guides pratiques téléchargeables à conserver aux côtés de votre projet.",
    guidesConversionTitle: "Guide de conversion WINDEV / WEBDEV",
    guidesConversionSummary: "Comment faire migrer une application WINDEV ou WEBDEV existante vers une stack moderne React et PostgreSQL, étape par étape.",
    guidesDraftBadge: "Brouillon",
    guidesDraftNote: "Ce guide est encore un brouillon. Les étapes sont utilisables, mais la formulation et les détails peuvent évoluer.",
    guidesLoading: "Chargement des guides…",
    guidesEmpty: "Aucun guide n'est encore disponible.",
    guidesLanguageLabel: "Langue",
    guidesLanguageAll: "Toutes les langues",
    faqHeading: "Foire aux questions",
    faqSubtitle: "Réponses aux questions fréquentes sur wxKanban.",
    faqEmpty: "Aucune FAQ n'est disponible pour le moment.",
    sessionsHeading: "Prochaines sessions de formation en ligne",
    sessionsSubtitle: "Participez à une session en direct animée par un formateur pour démarrer rapidement.",
    sessionsEmpty: "Aucune session n'est programmée pour le moment.",
    sessionsRegister: "Manifestez votre intérêt et nous vous préviendrons à l'ouverture de la prochaine session.",
    sessionsCta: "Contactez-nous pour vous inscrire",
    sessionsJoin: "S'inscrire",
    videosHeading: "Vidéos de formation",
    videosSubtitle: "Regardez des tutoriels pas à pas de wxKanban.",
    videosEmpty: "Aucune vidéo de formation n'est disponible pour le moment.",
    loading: "Chargement…",
    loadError: "Impossible de charger la documentation. Veuillez réessayer plus tard.",
    backAll: "← Toute la documentation",
    detailError: "Impossible de charger ce document. Veuillez réessayer plus tard.",
    notFoundTitle: "Introuvable",
    notFoundBody: "Ce document n'existe pas ou n'est pas encore publié.",
  },
  // [SCOPE 131 / T013] Suspend & cancel copy (FR-011, FR-009).
  subscriptionLifecycle: {
    sectionTitle: "Suspendre ou résilier",
    sectionDesc: "Suspendez la facturation pendant un temps, ou résiliez votre abonnement à la fin de la période déjà payée.",
    stateActive: "Votre abonnement est actif.",
    statePaused: "La facturation est suspendue.",
    statePausedDetail: "Aucune facture n'est émise et aucune donnée n'a été supprimée. Reprenez quand vous le souhaitez — vos projets et votre historique vous attendent.",
    stateCancelPending: "Résiliation programmée.",
    stateCancelPendingDetail: "Vous conservez un accès complet jusqu'au {{date}}. Vous pouvez revenir sur cette décision à tout moment avant cette date.",
    effectiveDateUnknown: "la fin de votre période de facturation en cours",
    pauseTitle: "Suspendre la facturation",
    pauseDesc: "Stripe cesse de vous facturer et votre équipe perd l'accès jusqu'à la reprise. Rien n'est supprimé et rien n'est programmé pour suppression.",
    pauseAction: "Suspendre la facturation",
    unpauseAction: "Reprendre la facturation",
    cancelTitle: "Résilier l'abonnement",
    cancelDesc: "Votre abonnement prend fin à l'épuisement de la période déjà payée. Vous conservez un accès complet jusque-là.",
    cancelAction: "Résilier l'abonnement",
    undoCancelAction: "Conserver mon abonnement",
    pauseSuccess: "Facturation suspendue. Reprenez quand vous le souhaitez.",
    unpauseSuccess: "Facturation reprise — bon retour parmi nous.",
    cancelSuccess: "Votre abonnement prendra fin le {{date}}. Vous pouvez revenir sur cette décision jusque-là.",
    undoCancelSuccess: "Résiliation annulée. Votre abonnement se poursuit normalement.",
    actionFailed: "L'opération n'a pas abouti et rien n'a été modifié. Veuillez réessayer.",
    dialogTitle: "Résilier votre abonnement ?",
    dialogBody: "Votre abonnement restera actif jusqu'au {{date}}. Ensuite votre équipe perdra l'accès, et 30 jours plus tard les données de votre entreprise seront archivées.",
    dialogUndoNote: "Vous pouvez revenir sur cette décision à tout moment avant le {{date}}.",
    dialogTypeLabel: "Saisissez {{name}} pour confirmer",
    dialogPlaceholder: "Nom de l'entreprise",
    dialogConfirm: "Résilier l'abonnement",
    dialogKeep: "Conserver mon abonnement",
    dialogMismatch: "Cela ne correspond pas au nom de votre entreprise.",
    wallPausedTitle: "La facturation est suspendue",
    wallPausedBody: "Votre entreprise a suspendu sa propre facturation, le produit est donc en pause. Rien n'a été supprimé. Un administrateur peut la reprendre depuis Paramètres → Facturation et abonnement.",
    wallPausedCta: "Accéder aux paramètres de facturation",
    wallPausedNonAdmin: "Votre entreprise a suspendu sa facturation. Rien n'a été supprimé — demandez à votre administrateur de la reprendre.",
    emailSubject: "Votre abonnement wxKanban prendra fin le {{date}}",
    emailHeading: "Votre résiliation est confirmée",
    emailBody: "Nous avons programmé la fin de votre abonnement wxKanban au {{date}}. Vous conservez un accès complet à {{company}} jusqu'à cette date et vous ne serez plus débité.",
    emailUndoHeading: "Vous avez changé d'avis ?",
    emailUndoBody: "Vous pouvez revenir sur cette décision à tout moment avant le {{date}} depuis Paramètres → Facturation et abonnement. Rien n'est supprimé tant que votre résiliation est en attente.",
    emailFooter: "Si vous n'êtes pas à l'origine de cette demande, contactez immédiatement votre administrateur.",
  },
  // [SCOPE 005 / T019] Time & Billing (SPEC-005). "Resource", never "AI" (constraint 5).
  timeBilling: {
    common: {
      cancel: "Annuler",
      close: "Fermer",
      delete: "Supprimer",
      edit: "Modifier",
      loading: "Chargement…",
      save: "Enregistrer",
      saving: "Enregistrement…",
    },
    page: {
      title: "Temps et facturation",
      open: "Ouvrir Temps et facturation",
      manualOnly: "Le temps des Ressources issu des exécutions d’agents n’est pas encore disponible. Saisissez le temps des Ressources manuellement.",
      engagedManual: "Pour les projets clients sur lesquels vous êtes engagé, le temps des Ressources se saisit uniquement manuellement.",
      generateStatement: "Générer un relevé",
      billedTotal: "Projets facturés sur cette période : {{hours}} h",
      actionFailed: "L’opération a échoué. Veuillez réessayer.",
      loadFailed: "Impossible de charger vos projets.",
    },
    range: {
      from: "Du",
      to: "Au",
      thisWeek: "Cette semaine",
      includeInvoiced: "Inclure le temps déjà facturé",
      invalid: "La date de début doit être antérieure ou égale à la date de fin.",
    },
    projects: {
      none: "Aucun projet pour le moment.",
      clientProject: "Client : {{client}}",
      hours: "{{hours}} h",
      billThis: "Facturable",
      entries: "Saisies",
      rates: "Tarifs",
      client: "Client",
      noClient: "— Aucun client —",
      billedTo: "Facturé à {{client}}",
      assignClient: "Attribuez un client pour facturer ce projet",
    },
    lines: {
      line: "Ligne",
      scope: "Scope",
      developer: "Développeur",
      resource: "Ressource",
      developerHours: "h Développeur",
      resourceHours: "h Ressource",
      actions: "Actions",
      addDeveloper: "Ajouter du temps Développeur",
      addResource: "Ajouter du temps Ressource",
      noScopes: "Ce projet n’a pas encore de scope.",
      deletedScope: "Scope supprimé",
      archived: "archivé",
      brainstorming: "Brainstorming",
      processes: "Processus",
      dba: "DBA",
      management: "Gestion",
      meetings: "Réunions",
      projectTotal: "Total du projet",
    },
    entry: {
      addDeveloper: "Ajouter du temps Développeur",
      addResource: "Ajouter du temps Ressource",
      editTitle: "Modifier la saisie de temps",
      person: "Qui a réalisé le travail",
      model: "Modèle",
      date: "Date",
      time: "Durée",
      timeHelp: "Saisissez 1:38, 1.5 ou 45m. Un signe moins en début de saisie crée une correction.",
      savedAs: "Enregistré : {{hours}} h (arrondi au quart d’heure supérieur).",
      notes: "Notes",
      correctionNeedsNote: "Une correction (durée négative) nécessite une note.",
      saveFailed: "Impossible d’enregistrer la saisie.",
    },
    entries: {
      title: "Saisies de temps — {{project}}",
      none: "Aucune saisie sur cette période.",
      who: "Qui / modèle",
      hours: "h",
      invoiced: "Facturé",
      invoicedHelp: "Cette saisie figure sur un relevé et ne peut plus être modifiée. Ajoutez une correction sur une période ultérieure.",
      confirmDelete: "Supprimer cette saisie ?",
      deleteFailed: "Impossible de supprimer la saisie.",
      loadFailed: "Impossible de charger les saisies.",
    },
    timer: {
      title: "Minuteur",
      idle: "Aucun minuteur en cours. Appuyez sur l'horloge d'un projet, ou sur ▶ sur une ligne, pour en démarrer un.",
      startTitle: "Démarrer le minuteur",
      chooseLine: "Ligne",
      startHelp: "Chaque démarrage et arrêt enregistre sa propre entrée : vous pouvez saisir plusieurs plages horaires dans une même journée.",
      runningHere: "Le minuteur est en cours sur ce projet.",
      start: "Démarrer le minuteur",
      stop: "Arrêter et enregistrer",
      alreadyRunning: "Un minuteur est déjà en cours.",
      willSave: "Sera enregistré : {{hours}} h.",
      autoNotes: "Exécutions d’agents pendant ce minuteur",
      autoUnavailable: "Les exécutions d’agents ne sont pas encore enregistrées.",
      noRuns: "Aucune exécution d’agent pour le moment.",
      refresh: "Actualiser les exécutions d’agents",
      manualNotes: "Vos notes",
      manualPlaceholder: "p. ex. appel de 15 minutes avec le client à propos du scope d’audit",
      notesFailed: "Impossible de charger les exécutions d’agents.",
      stopFailed: "Impossible d’arrêter le minuteur.",
      pause: "Pause",
      resume: "Reprendre",
      paused: "En pause : ce temps n’est pas facturé.",
      pauseFailed: "Impossible de mettre en pause ou de reprendre le minuteur.",
    },
    statement: {
      title: "Générer un relevé",
      description: "Créez un relevé pour un client et une période de facturation.",
      customer: "Client",
      noBilledProjects: "Ce client n’a pas encore de projet facturé",
      cycle: "Cycle de facturation",
      cycleWeekly: "Hebdomadaire",
      cycleBiweekly: "Toutes les deux semaines",
      periodStart: "Début de la période",
      periodEnd: "Fin de la période",
      periodNote: "Seul le temps facturé et non verrouillé de cette période est inclus. Les saisies générées passent en lecture seule.",
      generate: "Générer le relevé",
      working: "Traitement en cours…",
      created: "Relevé {{name}} créé",
      number: "Numéro du relevé",
      total: "Total",
      entries: "Saisies",
      downloadPdf: "Télécharger le PDF",
      periodFailed: "Impossible de charger la période suggérée",
      cycleFailed: "Impossible de modifier le cycle de facturation",
      generateFailed: "Impossible de générer le relevé",
      downloadFailed: "Impossible de télécharger le PDF",
    },
    rates: {
      title: "Tarifs du projet",
      unchanged: "Laisser vide pour conserver",
      useDefault: "Utiliser le tarif par défaut de l’entreprise",
      help: "Les nouvelles saisies utilisent le tarif en vigueur. Les saisies existantes conservent le tarif avec lequel elles ont été créées.",
      companyDefault: "tarif par défaut de l’entreprise",
      saved: "Enregistré. Développeur : {{developer}} · Ressource : {{resource}}",
      saveFailed: "Impossible d’enregistrer les tarifs.",
    },
    settings: {
      title: "Temps et facturation",
      description: "Tarifs horaires par défaut et coordonnées bancaires imprimées sur les relevés clients.",
      developerRate: "Tarif Développeur ($/h)",
      resourceRate: "Tarif Ressource ($/h)",
      ratesHelp: "Un projet peut remplacer l’un ou l’autre tarif. Modifier un tarif ne change pas les saisies existantes.",
      achTitle: "Détails du paiement ACH",
      achComplete: "Complet",
      achIncomplete: "Incomplet — les relevés indiqueront que l’ACH n’est pas configuré",
      bankName: "Nom de la banque",
      accountName: "Nom du compte",
      routingNumber: "Numéro d’acheminement",
      accountNumber: "Numéro de compte",
      endingIn: "se terminant par {{last4}}",
      notSet: "non défini",
      replacePlaceholder: "Saisissez un nouveau numéro pour le remplacer",
      achHelp: "Stocké chiffré. Seuls les 4 derniers chiffres sont affichés ici ; les numéros complets n’apparaissent que sur les relevés.",
      nothingChanged: "Rien à enregistrer.",
      saved: "Enregistré.",
      saveFailed: "Impossible d’enregistrer les paramètres.",
      loadFailed: "Impossible de charger les paramètres.",
    },
  },
};
